// src/data/dataService.js
// Production-ready database service for OurNet V08
// Clean implementation with proper error handling

// Import Supabase clients from shared location
// - supabase: Protected client (auth operations)
// - supabaseFetch: Direct REST API helper (reliable fallback when client hangs)
import { supabase, supabaseFetch, isDemoReadActive, isDemoRefusal } from '../lib/supabase';
import { getPublicProfiles } from './profileService';
import { demoGetGroupEventsByStartTime, demoAdjustMemberCount } from './demoReadService';
import { getFederationMembers } from '../utils/groupHelpers';
import { getBoundaryNamesInOrder, getBoundaryNames } from './directoryService';
import { pickGroupPlace, sharedParentId, acceptInvitationCode, GROUP_PLACE_MAX_AREAS } from '../utils/invitationRules';

// Log connection status on startup
console.log('📡 Supabase client initialized via shared import');

// Prevent concurrent queries
let getAllCommunitiesInProgress = null;

// Community Service - Full functionality
export const communityService = {
    async getAllCommunities() {
        // If a query is already in progress, return its result
        if (getAllCommunitiesInProgress) {
            console.log('🔧 getAllCommunities: Reusing in-progress query');
            return getAllCommunitiesInProgress;
        }

        console.log('🔧 Production: Starting getAllCommunities...');

        getAllCommunitiesInProgress = this._fetchAllCommunities();
        try {
            return await getAllCommunitiesInProgress;
        } finally {
            getAllCommunitiesInProgress = null;
        }
    },

    async _fetchAllCommunities() {
        try {
            // Use supabaseFetch for reliable direct REST API calls
            const { data, error } = await supabaseFetch.query('communities', {
                select: 'id,name,community_type,postcode,description',
                order: 'name'
            });

            if (error) {
                console.error('Error fetching communities:', error);
                return [];
            }

            console.log('📡 getAllCommunities: Got', data?.length || 0, 'communities');
            return data || [];
        } catch (error) {
            console.error('Exception in getAllCommunities:', error);
            return [];
        }
    },

    async getCommunityById(communityId) {
        try {
            const { data, error } = await supabaseFetch.query('communities', {
                select: '*',
                eq: { id: communityId },
                single: true
            });

            if (error) {
                console.error('Error fetching community by ID:', error);
                return null;
            }

            if (!data) return null;

            // Enrich with owner display name
            if (data.owner_id) {
                const { data: profiles } = await getPublicProfiles([data.owner_id]);
                const owner = profiles?.[0] || null;
                return { ...data, owner_display_name: owner?.display_name || null };
            }

            return data;
        } catch (error) {
            console.error('Exception in getCommunityById:', error);
            return null;
        }
    }
};

// Chat Service - V07 Architecture Compliant with Database Integration
export const chatService = {
    // Locality chats: community_id IS NULL, anchored by home_locality_id
    async getLocalityChats(boundaryIds, options = {}) {
        const { limit = 50 } = options;
        if (!boundaryIds || boundaryIds.length === 0) {
            return [];
        }

        try {
            console.log('🔧 Loading locality chats for', boundaryIds.length, 'boundaries');

            // Server-side boundary scoping via get_locality_chats RPC (2026-08-14 —
            // docs/briefs/2026-08-14-chat-boundary-scoped-fetch.md) — replaces a platform-wide
            // fetch of the 50 most recent rows of the entire chats table (locality chats, every
            // group's chats, every article/event comment mixed) followed by a client-side
            // filter, which Probe C (14 Aug) measured as losing 14 of 35 real locality chats
            // (40%) to unrelated content volume. The RPC does the boundary/demo/hidden
            // filtering server-side, same shape as get_articles_by_locality_v2.
            // One row beyond the display limit, same has-more mechanism as News/Events
            // (withHasMore) — dormant plumbing this build does not wire into any UI (Chat
            // renders its full array by design); sliced back to `limit` before the reply-count
            // pass below, so a discarded 51st row is never counted as a reply's parent either.
            const { data, error } = await supabaseFetch.rpc('get_locality_chats', {
                p_boundary_ids: boundaryIds,
                p_limit: limit + 1
            });

            if (error) {
                console.error('Error fetching locality chats:', error);
                return [];
            }

            const allChats = withHasMore(data || [], limit);
            const originalChats = allChats.filter(chat => !chat.parent_chat_id);

            // Compute reply counts from locality chats that are replies (live only — exclude deleted placeholders)
            const replyCounts = {};
            for (const chat of allChats) {
                if (chat.parent_chat_id && !chat.is_deleted_by_author) {
                    replyCounts[chat.parent_chat_id] = (replyCounts[chat.parent_chat_id] || 0) + 1;
                }
            }

            // Batch-fetch author display names
            const authorIds = [...new Set(originalChats.map(c => c.author_id).filter(Boolean))];
            let authorMap = {};
            if (authorIds.length > 0) {
                const { data: authors } = await supabaseFetch.rpc('get_public_profiles', {
                    p_user_ids: authorIds
                });
                authorMap = (authors || []).reduce((acc, u) => ({ ...acc, [u.id]: u.display_name }), {});
            }

            console.log(`✅ Found ${originalChats.length} locality chats`);

            const mappedChats = originalChats.map(chat => ({
                id: chat.id,
                headline: chat.headline,
                content: chat.content,
                author_id: chat.author_id,
                author_name: chat.is_author_removed ? null : (authorMap[chat.author_id] || chat.author_id),
                is_author_removed: chat.is_author_removed || false,
                is_deleted_by_author: chat.is_deleted_by_author || false,
                edited_at: chat.edited_at || null,
                community_id: chat.community_id,
                home_locality_id: chat.home_locality_id,
                reply_count: replyCounts[chat.id] || 0,
                like_count: chat.like_count || 0,
                dislike_count: 0,
                created_at: chat.created_at,
                updated_at: chat.updated_at
            }));
            mappedChats.hasMore = allChats.hasMore;
            return mappedChats;
        } catch (error) {
            console.error('Exception in getLocalityChats:', error);
            return [];
        }
    },

    async getOriginalChats(communityId) {
        try {
            console.log('🔧 Loading chats for community:', communityId);

            // Use supabaseFetch for reliable queries (supabase client can hang)
            const { data, error } = await supabaseFetch.query('chats', {
                select: 'id,headline,content,author_id,is_author_removed,is_deleted_by_author,community_id,parent_chat_id,like_count,created_at,updated_at',
                eq: { community_id: communityId },
                order: 'created_at.desc'
            });

            console.log('🔧 Chat query result:', { dataCount: data?.length, error });

            if (error) {
                console.error('Error fetching original chats:', error);
                return [];
            }

            // Filter for original chats only (no parent_chat_id) - done client-side
            // since supabaseFetch doesn't support is(null) filter easily
            const allChats = data || [];
            const originalChats = allChats.filter(chat => !chat.parent_chat_id);

            // Compute reply counts from the full array (live replies only — exclude deleted placeholders)
            const replyCounts = {};
            for (const chat of allChats) {
                if (chat.parent_chat_id && !chat.is_deleted_by_author) {
                    replyCounts[chat.parent_chat_id] = (replyCounts[chat.parent_chat_id] || 0) + 1;
                }
            }

            // Batch-fetch author display names
            const authorIds = [...new Set(originalChats.map(c => c.author_id).filter(Boolean))];
            let authorMap = {};
            if (authorIds.length > 0) {
                const { data: authors } = await getPublicProfiles(authorIds);
                authorMap = (authors || []).reduce((acc, u) => ({ ...acc, [u.id]: u.display_name }), {});
            }

            // Transform to V07 component expectations
            return originalChats.map(chat => ({
                id: chat.id,
                headline: chat.headline,
                content: chat.content,
                author_id: chat.author_id,
                author_name: chat.is_author_removed ? null : (authorMap[chat.author_id] || chat.author_id),
                is_author_removed: chat.is_author_removed || false,
                community_id: chat.community_id,
                reply_count: replyCounts[chat.id] || 0,
                like_count: chat.like_count || 0,
                dislike_count: 0,
                created_at: chat.created_at,
                updated_at: chat.updated_at
            }));
        } catch (error) {
            console.error('Exception in getOriginalChats:', error);
            return [];
        }
    },

    async getThreadChats(originalChatId) {
        try {
            // Use supabaseFetch to avoid JS client hanging on nested joins
            // First get the original chat to find its community_id
            const { data: originalChat, error: originalError } = await supabaseFetch.query('chats', {
                select: 'community_id',
                eq: { id: originalChatId },
                single: true
            });

            if (originalError || !originalChat) {
                console.error('Error fetching original chat:', originalError);
                return [];
            }

            // Get all chats from this community (supabaseFetch doesn't support 'not is null' filter)
            const { data, error } = await supabaseFetch.query('chats', {
                select: 'id,headline,content,author_id,is_author_removed,community_id,parent_chat_id,like_count,created_at,updated_at',
                eq: { community_id: originalChat.community_id },
                order: 'created_at.asc'
            });

            if (error) {
                console.error('Error fetching thread chats:', error);
                return [];
            }

            // Filter for chats with parent_chat_id (responses only)
            const responsesOnly = (data || []).filter(chat => chat.parent_chat_id);

            // Batch-fetch author display names
            const authorIds = [...new Set(responsesOnly.map(c => c.author_id).filter(Boolean))];
            let authorMap = {};
            if (authorIds.length > 0) {
                const { data: authors } = await getPublicProfiles(authorIds);
                authorMap = (authors || []).reduce((acc, u) => ({ ...acc, [u.id]: u.display_name }), {});
            }

            // Build a set of all chat IDs that belong to this thread
            const allChats = responsesOnly.map(chat => ({
                id: chat.id,
                headline: chat.headline,
                content: chat.content,
                author_id: chat.author_id,
                author_name: chat.is_author_removed ? null : (authorMap[chat.author_id] || chat.author_id),
                is_author_removed: chat.is_author_removed || false,
                community_id: chat.community_id,
                parent_chat_id: chat.parent_chat_id,
                like_count: chat.like_count || 0,
                dislike_count: 0,
                created_at: chat.created_at,
                updated_at: chat.updated_at
            }));

            // Filter to only include chats that belong to this thread
            // A chat belongs to this thread if its parent_chat_id is the original OR
            // if its parent_chat_id is in the thread
            const threadChatIds = new Set([originalChatId]);
            let foundNew = true;

            while (foundNew) {
                foundNew = false;
                for (const chat of allChats) {
                    if (!threadChatIds.has(chat.id) && threadChatIds.has(chat.parent_chat_id)) {
                        threadChatIds.add(chat.id);
                        foundNew = true;
                    }
                }
            }

            // Return only chats that are in this thread (excluding the original)
            return allChats.filter(chat => threadChatIds.has(chat.id));
        } catch (error) {
            console.error('Exception in getThreadChats:', error);
            return [];
        }
    },

    async getChatById(chatId) {
        try {
            // Use supabaseFetch to avoid JS client hanging on nested joins
            const { data, error } = await supabaseFetch.query('chats', {
                select: 'id,headline,content,author_id,is_author_removed,community_id,parent_chat_id,like_count,created_at,updated_at',
                eq: { id: chatId },
                single: true
            });

            if (error || !data) {
                console.error('Error fetching chat by id:', error);
                return null;
            }

            // Fetch author display name (skip if author was removed)
            let authorName = data.author_id;
            if (data.is_author_removed) {
                authorName = null;
            } else if (data.author_id) {
                const { data: profiles } = await getPublicProfiles([data.author_id]);
                authorName = profiles?.[0]?.display_name || data.author_id;
            }

            return {
                id: data.id,
                headline: data.headline,
                content: data.content,
                author_id: data.author_id,
                author_name: authorName,
                is_author_removed: data.is_author_removed || false,
                community_id: data.community_id,
                parent_chat_id: data.parent_chat_id,
                like_count: data.like_count || 0,
                dislike_count: 0,
                created_at: data.created_at,
                updated_at: data.updated_at
            };
        } catch (error) {
            console.error('Exception in getChatById:', error);
            return null;
        }
    },

    async createResponse(responseData) {
        try {
            // Use passed author_id (required - don't call auth.getUser which hangs)
            const userId = responseData.author_id;
            if (!userId) {
                console.error('createResponse: author_id is required');
                throw new Error('Author ID is required to post a response');
            }

            // Get auth token from localStorage (don't call auth.getSession which hangs)
            const authToken = await supabaseFetch.getAuthToken();

            if (!authToken) {
                console.error('createResponse: no auth token — session expired, refusing anon insert');
                const err = new Error('SESSION_EXPIRED');
                err.code = 'SESSION_EXPIRED';
                throw err;
            }

            // A reply carries its root's anchor, never the caller's: the caller's community_id /
            // home_locality_id are ignored whenever a parent is set (chat-replies-keep-their-
            // anchor brief). Without a parent this is not a reply, and the caller's value stands.
            const anchor = responseData.parent_chat_id
                ? await chatService._resolveRootAnchor(responseData.parent_chat_id)
                : { community_id: responseData.community_id ?? null, article_id: null, home_locality_id: null };

            // Use supabaseFetch to avoid hanging
            const insertResult = await supabaseFetch.insert('chats', {
                content: responseData.content,
                author_id: userId,
                community_id: anchor.community_id,
                article_id: anchor.article_id,
                home_locality_id: anchor.home_locality_id,
                parent_chat_id: responseData.parent_chat_id,
                chat_type: 'comment'
            }, { authToken });

            if (insertResult.error) {
                console.error('Error creating response:', insertResult.error);
                throw insertResult.error;
            }

            const data = insertResult.data;

            return {
                id: data?.id || crypto.randomUUID(),
                content: data?.content || responseData.content,
                author_id: data?.author_id || userId,
                author_name: 'Current User',
                community_id: data?.community_id ?? anchor.community_id,
                parent_chat_id: data?.parent_chat_id || responseData.parent_chat_id,
                like_count: 0,
                dislike_count: 0,
                created_at: data?.created_at || new Date().toISOString()
            };
        } catch (error) {
            console.error('Exception in createResponse:', error);
            throw error;
        }
    },

    async createOriginalChat(chatData) {
        try {
            // Use passed author_id (required - don't call auth.getUser which hangs)
            const userId = chatData.author_id;
            if (!userId) {
                console.error('createOriginalChat: author_id is required');
                throw new Error('Author ID is required to post a chat');
            }

            // Get auth token from localStorage (don't call auth.getSession which hangs)
            const authToken = await supabaseFetch.getAuthToken();

            if (!authToken) {
                console.error('createOriginalChat: no auth token — session expired, refusing anon insert');
                const err = new Error('SESSION_EXPIRED');
                err.code = 'SESSION_EXPIRED';
                throw err;
            }

            // Use supabaseFetch to avoid hanging
            const insertPayload = {
                headline: chatData.headline,
                content: chatData.content,
                author_id: userId,
                community_id: chatData.community_id || null,
                parent_chat_id: null,
                chat_type: 'comment'
            };
            // Locality chats: anchor by home_locality_id when community_id is null
            if (chatData.home_locality_id) {
                insertPayload.home_locality_id = chatData.home_locality_id;
            }
            const insertResult = await supabaseFetch.insert('chats', insertPayload, { authToken });

            if (insertResult.error) {
                console.error('Error creating original chat:', insertResult.error);
                throw insertResult.error;
            }

            const data = insertResult.data;

            return {
                id: data?.id || crypto.randomUUID(),
                headline: data?.headline || chatData.headline,
                content: data?.content || chatData.content,
                author_id: data?.author_id || userId,
                author_name: 'Current User',
                community_id: data?.community_id || chatData.community_id,
                like_count: 0,
                dislike_count: 0,
                created_at: data?.created_at || new Date().toISOString()
            };
        } catch (error) {
            console.error('Exception in createOriginalChat:', error);
            throw error;
        }
    },

    // Fetch all responses for a root chat by walking parent_chat_id chain.
    // Works for both locality chats (community_id NULL) and group chats.
    async getResponsesByRoot(rootChatId) {
        try {
            // Level 1: direct children of the root
            const { data: level1, error: err1 } = await supabaseFetch.query('chats', {
                select: 'id,headline,content,author_id,is_author_removed,is_deleted_by_author,edited_at,community_id,home_locality_id,parent_chat_id,like_count,created_at,updated_at,is_hidden',
                eq: { parent_chat_id: rootChatId },
                order: 'created_at.asc'
            });

            if (err1) {
                console.error('Error fetching level-1 responses:', err1);
                return [];
            }

            const allResponses = [...(level1 || [])];
            const level1Ids = (level1 || []).map(c => c.id);

            // Level 2+: children of level-1 (covers the single depth-2 row in production)
            if (level1Ids.length > 0) {
                const { data: level2, error: err2 } = await supabaseFetch.query('chats', {
                    select: 'id,headline,content,author_id,is_author_removed,is_deleted_by_author,edited_at,community_id,home_locality_id,parent_chat_id,like_count,created_at,updated_at,is_hidden',
                    inFilter: { parent_chat_id: level1Ids },
                    order: 'created_at.asc'
                });

                if (!err2 && level2) {
                    allResponses.push(...level2);
                }
            }

            // Filter out hidden responses
            const visible = allResponses.filter(c => !c.is_hidden);

            // Batch-fetch author display names
            const authorIds = [...new Set(visible.map(c => c.author_id).filter(Boolean))];
            let authorMap = {};
            if (authorIds.length > 0) {
                const { data: authors } = await getPublicProfiles(authorIds);
                authorMap = (authors || []).reduce((acc, u) => ({ ...acc, [u.id]: u.display_name }), {});
            }

            return visible.map(chat => ({
                id: chat.id,
                headline: chat.headline,
                content: chat.content,
                author_id: chat.author_id,
                author_name: chat.is_author_removed ? null : (authorMap[chat.author_id] || chat.author_id),
                is_author_removed: chat.is_author_removed || false,
                is_deleted_by_author: chat.is_deleted_by_author || false,
                edited_at: chat.edited_at || null,
                community_id: chat.community_id,
                home_locality_id: chat.home_locality_id,
                parent_chat_id: chat.parent_chat_id,
                like_count: chat.like_count || 0,
                dislike_count: 0,
                created_at: chat.created_at,
                updated_at: chat.updated_at
            }));
        } catch (error) {
            console.error('Exception in getResponsesByRoot:', error);
            return [];
        }
    },

    // Article/Event Comment Functions
    async getArticleComments(articleId) {
        try {
            // Widened (June 2026) to feed the shared ConcertinaThread engine: comments now carry
            // is_deleted_by_author, edited_at and headline so they get the same gravestone, the
            // "(edited)" marker and inline edit/delete that locality Chat has. Fields already exist
            // on `chats` — query widening only, no migration.
            const { data, error } = await supabaseFetch.query('chats', {
                select: 'id,headline,content,author_id,is_author_removed,is_deleted_by_author,edited_at,article_id,parent_chat_id,like_count,created_at,updated_at',
                eq: { article_id: articleId },
                order: 'created_at.asc'
            });

            if (error) {
                console.error('Error fetching article comments:', error);
                return [];
            }

            // Batch-fetch author display names
            const authorIds = [...new Set((data || []).map(c => c.author_id).filter(Boolean))];
            let authorMap = {};
            if (authorIds.length > 0) {
                const { data: authors } = await getPublicProfiles(authorIds);
                authorMap = (authors || []).reduce((acc, u) => ({ ...acc, [u.id]: u.display_name }), {});
            }

            return (data || []).map(comment => {
                const displayName = comment.is_author_removed ? null : (authorMap[comment.author_id] || comment.author_id);
                return {
                    id: comment.id,
                    headline: comment.headline,
                    content: comment.content,
                    author_id: comment.author_id,
                    // Engine-shaped fields (match getResponsesByRoot)
                    author_name: displayName,
                    is_author_removed: comment.is_author_removed || false,
                    is_deleted_by_author: comment.is_deleted_by_author || false,
                    edited_at: comment.edited_at || null,
                    article_id: comment.article_id,
                    parent_chat_id: comment.parent_chat_id,
                    like_count: comment.like_count || 0,
                    dislike_count: 0,
                    created_at: comment.created_at,
                    updated_at: comment.updated_at,
                    // Backward-compatible aliases (legacy callers)
                    author: displayName,
                    parent_id: comment.parent_chat_id,
                    likes: comment.like_count || 0,
                    replies: []
                };
            });
        } catch (error) {
            console.error('Exception in getArticleComments:', error);
            return [];
        }
    },

    async createArticleComment(commentData) {
        try {
            // Use provided author_id (required - don't call auth.getUser which hangs)
            const authorId = commentData.author_id;
            if (!authorId) {
                console.error('createArticleComment: author_id is required');
                throw new Error('Author ID is required to post a comment');
            }

            // Get auth token from localStorage (don't call auth.getSession which hangs)
            const authToken = await supabaseFetch.getAuthToken();

            // A reply to a comment carries its root's anchor, never the caller's
            // (chat-replies-keep-their-anchor brief). A top-level comment keeps the caller's
            // article_id.
            let articleId = commentData.article_id;
            if (commentData.parent_chat_id) {
                const anchor = await chatService._resolveRootAnchor(commentData.parent_chat_id);
                if (anchor.article_id !== commentData.article_id) {
                    console.warn('createArticleComment: caller article_id', commentData.article_id,
                        'differs from the thread root\'s', anchor.article_id, '— using the root\'s');
                }
                articleId = anchor.article_id;
            }

            // Use supabaseFetch (native fetch) instead of Supabase JS client
            // The JS client hangs on insert to chats table
            const insertResult = await supabaseFetch.insert('chats', {
                content: commentData.content,
                author_id: authorId,
                article_id: articleId,
                parent_chat_id: commentData.parent_chat_id || null,
                chat_type: 'comment'
            }, { authToken });

            if (insertResult.error) {
                console.error('Error creating article comment:', insertResult.error);
                throw insertResult.error;
            }

            const data = insertResult.data;

            // Note: increment_article_comment_count RPC doesn't exist yet
            // Comment count can be derived from querying chats where article_id matches

            return {
                id: data?.id || crypto.randomUUID(),
                content: data?.content || commentData.content,
                author_id: data?.author_id || authorId,
                author: 'Current User',
                article_id: data?.article_id || articleId,
                parent_id: data?.parent_chat_id || commentData.parent_chat_id,
                likes: data?.like_count || 0,
                created_at: data?.created_at || new Date().toISOString(),
                replies: []
            };
        } catch (error) {
            console.error('Exception in createArticleComment:', error);
            throw error;
        }
    },

    async updateChat({ chatId, headline, content }) {
        try {
            if (!chatId) throw new Error('chatId is required');
            if (!content || content.length < 10) throw new Error('Content must be at least 10 characters');
            if (content.length > 5000) throw new Error('Content must be at most 5000 characters');
            if (headline !== undefined && headline !== null) {
                if (headline.length < 3) throw new Error('Headline must be at least 3 characters');
                if (headline.length > 300) throw new Error('Headline must be at most 300 characters');
            }

            const authToken = await supabaseFetch.getAuthToken();
            if (!authToken) throw new Error('Authentication required');

            const updatePayload = {
                content,
                edited_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            };
            // Only set headline for top-level posts (headline is undefined/null for replies)
            if (headline !== undefined) {
                updatePayload.headline = headline;
            }

            const { data, error } = await supabaseFetch.update('chats', updatePayload, {
                eq: { id: chatId },
                authToken
            });

            if (error) {
                console.error('Error updating chat:', error);
                throw error;
            }

            return data;
        } catch (error) {
            console.error('Exception in updateChat:', error);
            throw error;
        }
    },

    async deleteChat({ chatId }) {
        try {
            if (!chatId) throw new Error('chatId is required');

            const authToken = await supabaseFetch.getAuthToken();
            if (!authToken) throw new Error('Authentication required');

            // Fetch this chat's parent_chat_id before any deletion (needed for orphan cleanup)
            const { data: chatRow, error: fetchError } = await supabaseFetch.query('chats', {
                select: 'parent_chat_id',
                eq: { id: chatId },
                single: true
            });

            if (fetchError) {
                console.error('Error fetching chat for delete:', fetchError);
                throw fetchError;
            }

            const parentChatId = chatRow?.parent_chat_id || null;

            // Check the ENTIRE subtree for any live descendant at any depth.
            // If even one exists, the current row must soft-delete (keep as anchor).
            const hasLiveDescendant = await this._hasLiveDescendant(chatId);

            if (!hasLiveDescendant) {
                // Hard delete: entire subtree is dead (or empty), row can be removed
                // First, recursively remove any remaining soft-deleted child rows (FK requires no children)
                await this._hardDeleteDescendants(chatId, authToken);

                // message_engagement rows cascade automatically via FK
                const { error } = await supabaseFetch.delete('chats', {
                    eq: { id: chatId },
                    authToken
                });

                if (error) {
                    console.error('Error hard-deleting chat:', error);
                    throw error;
                }

                // Walk up and clean orphaned parents after hard-delete
                if (parentChatId) {
                    await this._cleanOrphanedParent(parentChatId, authToken);
                }

                return { action: 'hard_deleted' };
            } else {
                // Soft delete: has live replies, keep row as thread anchor with content wiped
                const { error } = await supabaseFetch.update('chats', {
                    is_deleted_by_author: true,
                    content: '',
                    headline: null,
                    updated_at: new Date().toISOString()
                }, {
                    eq: { id: chatId },
                    authToken
                });

                if (error) {
                    console.error('Error soft-deleting chat:', error);
                    throw error;
                }

                return { action: 'soft_deleted' };
            }
        } catch (error) {
            console.error('Exception in deleteChat:', error);
            throw error;
        }
    },

    /**
     * Resolve the anchor a reply must carry: the anchor of the thread's ROOT message
     * (community_id for a group chat, article_id for a comment, home_locality_id for a locality
     * chat — docs/briefs/2026-09-27-chat-replies-keep-their-anchor.md). Walks parent_chat_id up
     * to the root. Throws (code ROOT_UNREADABLE) if any hop cannot be read — a reply is never
     * inserted unanchored or with anchors guessed from the caller. An unreadable root includes a
     * hidden locality root, or a group root the replier is not a member of: refusals by design.
     */
    async _resolveRootAnchor(parentChatId) {
        const MAX_HOPS = 50;
        let id = parentChatId;
        for (let hop = 0; hop < MAX_HOPS && id; hop++) {
            const { data: row, error } = await supabaseFetch.query('chats', {
                select: 'id,parent_chat_id,community_id,article_id,home_locality_id',
                eq: { id },
                single: true
            });
            if (error || !row) {
                console.error('_resolveRootAnchor: could not read chat', id, error);
                const err = new Error('ROOT_UNREADABLE');
                err.code = 'ROOT_UNREADABLE';
                throw err;
            }
            if (!row.parent_chat_id) {
                return {
                    community_id: row.community_id ?? null,
                    article_id: row.article_id ?? null,
                    home_locality_id: row.home_locality_id ?? null
                };
            }
            id = row.parent_chat_id;
        }
        console.error('_resolveRootAnchor: no root within', MAX_HOPS, 'hops from', parentChatId);
        const err = new Error('ROOT_UNREADABLE');
        err.code = 'ROOT_UNREADABLE';
        throw err;
    },

    /**
     * Walk up the parent chain and hard-delete any orphaned soft-deleted ancestors.
     * A parent is orphaned when it is soft-deleted (is_deleted_by_author = true) AND
     * has zero remaining child rows (not just zero live children — the FK requires
     * no child rows at all before a row can be hard-deleted).
     */
    async _cleanOrphanedParent(parentId, authToken) {
        try {
            // Fetch the parent row
            const { data: parent, error: fetchErr } = await supabaseFetch.query('chats', {
                select: 'id,parent_chat_id,is_deleted_by_author',
                eq: { id: parentId },
                single: true
            });

            if (fetchErr || !parent) return; // Parent already gone or fetch failed — nothing to do

            // Only clean up if the parent is itself soft-deleted
            if (!parent.is_deleted_by_author) return;

            // Check if the parent has ANY remaining child rows (live or deleted)
            const { data: children, error: childErr } = await supabaseFetch.query('chats', {
                select: 'id',
                eq: { parent_chat_id: parentId }
            });

            if (childErr) return;

            if ((children || []).length > 0) return; // Still has children — cannot hard-delete

            // Hard-delete the orphaned parent
            const { error: delErr } = await supabaseFetch.delete('chats', {
                eq: { id: parentId },
                authToken
            });

            if (delErr) {
                console.error('Error cleaning orphaned parent chat:', delErr);
                return;
            }

            // Continue walking up if this parent also had a parent
            if (parent.parent_chat_id) {
                await this._cleanOrphanedParent(parent.parent_chat_id, authToken);
            }
        } catch (error) {
            console.error('Exception in _cleanOrphanedParent:', error);
            // Non-fatal: the render backstop will hide any remaining orphans
        }
    },

    /**
     * Recursively check whether any descendant of `parentId` is live
     * (is_deleted_by_author = false). Short-circuits on the first live row found.
     */
    async _hasLiveDescendant(parentId) {
        const { data: children } = await supabaseFetch.query('chats', {
            select: 'id,is_deleted_by_author',
            eq: { parent_chat_id: parentId }
        });

        for (const child of (children || [])) {
            if (!child.is_deleted_by_author) return true;
            if (await this._hasLiveDescendant(child.id)) return true;
        }
        return false;
    },

    /**
     * Recursively hard-delete all descendant rows of a chat (depth-first).
     * SAFETY: Only called after _hasLiveDescendant confirmed the entire subtree
     * is dead. Defensive assertion aborts if a live row is encountered.
     */
    async _hardDeleteDescendants(parentId, authToken) {
        const { data: children } = await supabaseFetch.query('chats', {
            select: 'id,is_deleted_by_author',
            eq: { parent_chat_id: parentId }
        });

        for (const child of (children || [])) {
            // Defensive: abort if a live row is found (should never happen after guard)
            if (!child.is_deleted_by_author) {
                console.error('_hardDeleteDescendants encountered live row', child.id, '— aborting');
                throw new Error('Cannot hard-delete subtree containing live row ' + child.id);
            }
            // Depth-first: remove grandchildren before child
            await this._hardDeleteDescendants(child.id, authToken);
            await supabaseFetch.delete('chats', {
                eq: { id: child.id },
                authToken
            });
        }
    },

    async likeComment(commentId) {
        try {
            // First get current like count
            const { data: chat, error: fetchError } = await supabaseFetch.query('chats', {
                select: 'like_count',
                eq: { id: commentId },
                single: true
            });

            if (fetchError) {
                console.error('Error fetching comment:', fetchError);
                throw fetchError;
            }

            // Increment and update
            const newCount = (chat?.like_count || 0) + 1;
            const { error } = await supabaseFetch.update('chats',
                { like_count: newCount },
                { eq: { id: commentId } }
            );

            if (error) {
                console.error('Error liking comment:', error);
                throw error;
            }
        } catch (error) {
            console.error('Exception in likeComment:', error);
            throw error;
        }
    }
};

// Haversine distance calculation (returns distance in km)
function haversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in km
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) *
        Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in km
}

// Fetches one row beyond `limit` and slices it back off, recording whether that extra row
// existed as a `hasMore` property on the returned array — riding the same array reference every
// existing caller already threads straight into React state (setArticles/setEvents), rather than
// changing what getLocalityNews/getLocalityEvents return. One mechanism, shared by News and
// Events. See docs/briefs/2026-08-13-has-more-signal.md.
function withHasMore(items, limit) {
    const hasMore = items.length > limit;
    const sliced = items.slice(0, limit);
    sliced.hasMore = hasMore;
    return sliced;
}

// MVP Geocentric Article Service
// Uses PostGIS spatial queries for distance-based content discovery

export const articleService = {
    // Get locality news - uses locality_ids array overlap via get_articles_by_locality RPC
    // Options: { boundaryIds: BIGINT[], guestCoords: { lat, lng }, limit: number }
    // limit (default 50) is how many to show; Load More re-asks with it raised by 50 (Roadmap #104,
    // docs/briefs/2026-10-18-feed-dam-ranking-phase2-events-addendum-5.md). The feed's rank order is
    // deterministic, so a larger limit returns the smaller one's rows plus the next by rank.
    async getLocalityNews(userHomeAreaId = null, options = {}) {
        const { boundaryIds = null, guestCoords = null, limit = 50 } = options;
        const NEWS_FETCH_LIMIT = limit;

        try {
            // Case 1: Have boundary_ids - use locality_ids array overlap via RPC
            if (boundaryIds && boundaryIds.length > 0) {
                console.log('🗺️ Loading articles for', boundaryIds.length, 'boundaries via get_articles_by_locality_v2');

                // Fetch one row beyond the display limit — its presence (or absence) answers
                // "does more exist?" for free, with no RPC signature change. withHasMore slices
                // it back off before anything downstream sees it.
                const { data, error } = await supabaseFetch.rpc('get_articles_by_locality_v2', {
                    p_saved_ids: boundaryIds,
                    p_limit: NEWS_FETCH_LIMIT + 1,
                    p_article_types: ['news', 'website']
                });

                if (error) {
                    console.error('❌ Error fetching articles for boundaries:', error);
                    return []; // No fallback - user needs to set their location
                }

                const case1Result = withHasMore((data || []).map(article => ({
                    id: article.id,
                    title: article.title,
                    content: article.content,
                    summary: article.summary,
                    article_type: article.article_type,
                    cover_image_url: article.cover_image_url,
                    hero_image_thumbnail_url: article.hero_image_thumbnail_url || null,
                    video_url: article.video_url || null,
                    author_id: article.author_id,
                    author_name: article.author_name || 'Unknown Author',
                    community_id: article.community_id,
                    group_name: article.group_name || null,
                    location_postcode: article.location_postcode,
                    latitude: article.latitude,
                    longitude: article.longitude,
                    reputation_score: article.reputation_score || 0,
                    nearest_locality: Array.isArray(article.matching_locality_names) && article.matching_locality_names.length > 0
                        ? article.matching_locality_names.join(', ')
                        : null,
                    view_count: article.view_count || 0,
                    like_count: article.like_count || 0,
                    comment_count: article.comment_count || 0,
                    published_at: article.published_at,
                    created_at: article.created_at
                })), NEWS_FETCH_LIMIT);
                console.log(`✅ Found ${case1Result.length} articles within user boundaries (more available: ${case1Result.hasMore})`);
                return case1Result;
            }

            // Case 2: No boundary_ids but have IP-detected coordinates — resolve to boundary, then query
            if (guestCoords && guestCoords.lat && guestCoords.lng) {
                console.log('🌍 Guest IP fallback - resolving coordinates to boundary:', guestCoords.lat, guestCoords.lng);
                try {
                    // Resolve IP-derived lat/lng to the most specific containing boundary
                    const { data: boundaryId, error: resolveError } = await supabaseFetch.rpc('resolve_point_to_boundary', {
                        p_lat: guestCoords.lat,
                        p_lng: guestCoords.lng
                    });

                    if (resolveError) {
                        console.warn('⚠️ resolve_point_to_boundary failed:', resolveError.message);
                        // Fall through to Case 3
                    } else if (boundaryId) {
                        console.log(`🌍 Resolved to boundary ID: ${boundaryId}`);

                        // Fetch articles for the resolved boundary — one row beyond the display
                        // limit, per the same has-more mechanism as Case 1.
                        const { data, error } = await supabaseFetch.rpc('get_articles_by_locality_v2', {
                            p_saved_ids: [boundaryId],
                            p_limit: NEWS_FETCH_LIMIT + 1,
                            p_article_types: ['news', 'website']
                        });

                        if (!error && data) {
                            const case2Result = withHasMore((data || []).map(article => ({
                                id: article.id,
                                title: article.title,
                                content: article.content,
                                summary: article.summary,
                                article_type: article.article_type,
                                cover_image_url: article.cover_image_url,
                                hero_image_thumbnail_url: article.hero_image_thumbnail_url || null,
                                video_url: article.video_url || null,
                                author_id: article.author_id,
                                author_name: article.author_name || 'Unknown Author',
                                community_id: article.community_id,
                                group_name: article.group_name || null,
                                location_postcode: article.location_postcode,
                                latitude: article.latitude,
                                longitude: article.longitude,
                                reputation_score: article.reputation_score || 0,
                                nearest_locality: Array.isArray(article.matching_locality_names) && article.matching_locality_names.length > 0
                                    ? article.matching_locality_names.join(', ')
                                    : null,
                                view_count: article.view_count || 0,
                                like_count: article.like_count || 0,
                                comment_count: article.comment_count || 0,
                                published_at: article.published_at,
                                created_at: article.created_at
                            })), NEWS_FETCH_LIMIT);
                            console.log(`✅ Found ${case2Result.length} articles for guest boundary (more available: ${case2Result.hasMore})`);
                            return case2Result;
                        } else if (error) {
                            console.warn('⚠️ get_articles_by_locality failed for guest:', error.message);
                            // Fall through to Case 3
                        }
                    } else {
                        console.log('⚠️ IP coordinates not within any known boundary - falling through to all public articles');
                        // Fall through to Case 3
                    }
                } catch (err) {
                    console.warn('⚠️ Guest IP fallback failed:', err.message);
                    // Fall through to Case 3
                }
            }

            // Case 3: No location at all — show public, published GROUP articles only.
            // Architect ruling, 18 Sep 2026 (docs/escalations/2026-09-18-phase-b1-schema-targeting-design.md
            // §7, option iii): keep the behaviour this path has always had, without the is_demo
            // pre-query that produced it. That pre-query fetched every non-demo community id and
            // filtered articles with community_id IN (…) — which can never match NULL, so it also
            // excluded every locality article (community_id NULL). Since 20260918d/e no public row can
            // be demo, so its only live effect was that exclusion; community_id NOT NULL is the same
            // set (the FK guarantees every non-NULL community_id is a real group). Whether Case 3
            // should also show locality content is an open product question, not decided here.
            console.warn('⚠️  No location available - showing public group articles');

            // One row beyond the display limit, per the same has-more mechanism as Cases 1 and 2,
            // for consistency across every path through this function. Sliced immediately, before
            // the author/group enrichment lookups below, so a discarded 51st row's author/community
            // never reaches those queries either — not just the final returned array.
            //
            // Group content follows the group (follow-ups addendum 1, Part 1, ruled 27 Sep 2026): a
            // group post with no locality stays in its group, so it is left out here unless it is
            // platform content; and a soft-deleted group's posts are left out (KNOWN_ISSUES.md:39).
            // The group filter needs the embed: RLS hides soft-deleted groups from visitors, so the
            // client cannot list them to exclude, and a post-filter would break the has-more
            // contract. A supabaseFetch embed is a design choice argued on its merits
            // (Architecture.md § "On nested joins"); communities!inner is already used live by
            // searchArticlesByTags. The explicit deleted_at filter also covers an owner, who can
            // still see their own soft-deleted group under RLS. The embed carries the group name.
            const { data: fetchedData, error } = await supabaseFetch.query('articles', {
                select: 'id,title,content,summary,article_type,cover_image_url,hero_image_thumbnail_url,video_url,author_id,community_id,location_postcode,latitude,longitude,reputation_score,view_count,like_count,comment_count,published_at,created_at,communities!inner(name,deleted_at)',
                eq: { is_public: true, is_published: true },
                is: { 'communities.deleted_at': null },
                notNull: ['community_id'],
                or: 'home_locality_id.not.is.null,is_platform_content.is.true',
                order: 'published_at.desc',
                limit: NEWS_FETCH_LIMIT + 1
            });

            if (error) {
                console.error('❌ Error fetching all public articles:', error);
                return [];
            }

            const data = withHasMore(fetchedData || [], NEWS_FETCH_LIMIT);

            console.log(`✅ Found ${data.length} public articles (no location filter, group articles only; more available: ${data.hasMore})`);

            // Need to add author names manually since we're not using the RPC (group names come
            // from the embed above)
            const authorIds = [...new Set((data || []).map(a => a.author_id).filter(Boolean))];

            let authorMap = {};

            if (authorIds.length > 0) {
                const { data: authors } = await supabaseFetch.rpc('get_public_profiles', {
                    p_user_ids: authorIds
                });
                authorMap = (authors || []).reduce((acc, u) => ({ ...acc, [u.id]: u.display_name }), {});
            }

            const mappedFallbackArticles = (data || []).map(article => ({
                id: article.id,
                title: article.title,
                content: article.content,
                summary: article.summary,
                article_type: article.article_type,
                cover_image_url: article.cover_image_url,
                hero_image_thumbnail_url: article.hero_image_thumbnail_url || null,
                video_url: article.video_url || null,
                author_id: article.author_id,
                author_name: authorMap[article.author_id] || 'Unknown Author',
                community_id: article.community_id,
                group_name: article.communities?.name || null,
                location_postcode: article.location_postcode,
                latitude: article.latitude,
                longitude: article.longitude,
                reputation_score: article.reputation_score || 0,
                nearest_locality: null, // No locality matching in this mode
                view_count: article.view_count || 0,
                like_count: article.like_count || 0,
                comment_count: article.comment_count || 0,
                published_at: article.published_at,
                created_at: article.created_at
            }));
            mappedFallbackArticles.hasMore = data.hasMore;
            return mappedFallbackArticles;
        } catch (error) {
            console.error('Exception in getLocalityNews:', error);
            return [];
        }
    },

    // Get articles for a specific group (both public and private)
    async getGroupArticles(communityId) {
        const GROUP_ARTICLES_FETCH_LIMIT = 50;
        try {
            if (!communityId) {
                console.warn('⚠️  No community ID provided to getGroupArticles');
                return [];
            }

            console.log('🔧 Loading articles for group:', communityId);

            // One row beyond the display limit, same has-more mechanism as getLocalityNews/
            // getLocalityEvents (docs/briefs/2026-08-13-has-more-signal.md) — Group-context News
            // shares the More control with Locality News (2026-08-13-more-control-phase-a.md §3),
            // so it needs the same honest signal, not just the same UI.
            const { data, error } = await supabaseFetch.query('articles', {
                select: 'id,title,content,summary,article_type,cover_image_url,hero_image_thumbnail_url,video_url,website_url,author_id,community_id,location_postcode,view_count,like_count,comment_count,published_at,created_at,is_public',
                eq: { community_id: communityId, is_published: true },
                inFilter: { article_type: ['news', 'website'] },
                order: 'published_at.desc',
                limit: GROUP_ARTICLES_FETCH_LIMIT + 1
            });

            console.log('🔧 Group articles query result:', { dataCount: data?.length, error });

            if (error) {
                console.error('Error fetching group articles:', error);
                return [];
            }

            // Batch-fetch author display names and community name
            const authorIds = [...new Set((data || []).map(a => a.author_id).filter(Boolean))];
            let authorMap = {};
            if (authorIds.length > 0) {
                const { data: authors } = await getPublicProfiles(authorIds);
                authorMap = (authors || []).reduce((acc, u) => ({ ...acc, [u.id]: u.display_name }), {});
            }
            let groupName = communityId;
            const { data: community } = await supabaseFetch.query('communities', {
                select: 'name',
                eq: { id: communityId },
                single: true
            });
            if (community?.name) groupName = community.name;

            const mapped = withHasMore((data || []).map(article => ({
                id: article.id,
                title: article.title,
                content: article.content,
                summary: article.summary,
                article_type: article.article_type,
                cover_image_url: article.cover_image_url,
                hero_image_thumbnail_url: article.hero_image_thumbnail_url || null,
                video_url: article.video_url || null,
                website_url: article.website_url || null,
                author_id: article.author_id,
                author_name: authorMap[article.author_id] || article.author_id,
                community_id: article.community_id,
                group_name: groupName,
                location_postcode: article.location_postcode,
                view_count: article.view_count || 0,
                like_count: article.like_count || 0,
                comment_count: article.comment_count || 0,
                published_at: article.published_at,
                created_at: article.created_at,
                is_public: article.is_public
            })), GROUP_ARTICLES_FETCH_LIMIT);

            // Client-side pin: hoist website-embed article(s) to the top of the group feed,
            // preserving published_at.desc order within each group. ~one 'website' per group.
            const pinned = [
                ...mapped.filter(a => a.article_type === 'website'),
                ...mapped.filter(a => a.article_type !== 'website')
            ];
            pinned.hasMore = mapped.hasMore;
            return pinned;
        } catch (error) {
            console.error('Exception in getGroupArticles:', error);
            return [];
        }
    },

    async getArticleById(articleId) {
        console.log('🔧 getArticleById: Fetching article:', articleId);
        try {
            // Use supabaseFetch for all queries - Supabase JS client hangs on nested joins
            // Step 1: Fetch article
            console.log('🔧 getArticleById: Fetching article data via supabaseFetch...');
            const { data: article, error: articleError } = await supabaseFetch.query('articles', {
                select: '*',
                eq: { id: articleId },
                single: true
            });

            console.log('🔧 getArticleById: Article query complete, error:', articleError, 'hasData:', !!article);

            if (articleError || !article) {
                console.error('Error fetching article by ID:', articleError);
                return null;
            }

            // Step 2: Fetch community data if article has a community_id
            let community = null;
            if (article.community_id) {
                const { data: communityData } = await supabaseFetch.query('communities', {
                    select: 'name, community_type, description, postcode, slug, logo_url',
                    eq: { id: article.community_id },
                    single: true
                });
                community = communityData;
            }

            // Step 3: Fetch author data if article has an author_id
            let author = null;
            if (article.author_id) {
                const { data: profiles } = await getPublicProfiles([article.author_id]);
                author = profiles?.[0] || null;
            }

            // Step 4: Resolve locality name for locality articles (no community_id)
            let localityName = null;
            if (!article.community_id && article.home_locality_id) {
                const { data: boundaries } = await supabaseFetch.rpc('get_boundaries_by_ids', {
                    p_ids: [article.home_locality_id]
                });
                localityName = boundaries?.[0]?.name || null;
            }

            console.log('🔧 getArticleById: Article fetched successfully:', article.title);
            return {
                ...article,
                author_name: author?.display_name || 'Unknown Author',
                // Group fields — only populated when article belongs to a group
                group_name: community?.name || null,
                group_description: community?.description || '',
                group_slug: community?.slug,
                group_postcode: community?.postcode,
                group_logo_url: community?.logo_url || null,
                group_type: community?.community_type,
                // Locality name — resolved from home_locality_id for locality articles
                locality_name: localityName
            };
        } catch (error) {
            console.error('Exception in getArticleById:', error);
            return null;
        }
    },

    async incrementViewCount(articleId) {
        // increment_view_count RPC does not exist in the database yet.
        // A DB migration is needed to create it before this can be wired up.
        // No-op for now to avoid 404 console noise.
    },

    // Search articles by tags
    // Tags are comma-separated, case-insensitive
    // Returns articles ranked by number of matching tags (relevance)
    async searchArticlesByTags(tags, options = {}) {
        try {
            const { limit = 50 } = options;

            // Parse tags (comma-separated, trim, uppercase)
            const tagArray = tags.split(',')
                .map(t => t.trim().toUpperCase())
                .filter(t => t.length > 0);

            if (tagArray.length === 0) {
                console.log('🔍 searchArticlesByTags: No valid tags provided');
                return [];
            }

            console.log('🔍 searchArticlesByTags: Searching for tags:', tagArray);

            // Query published public articles only
            const { data: articles, error } = await supabaseFetch.query('articles', {
                select: 'id,title,content,summary,article_type,cover_image_url,hero_image_thumbnail_url,video_url,author_id,community_id,location_postcode,latitude,longitude,reputation_score,view_count,like_count,comment_count,published_at,created_at,article_tags,communities!inner(name,community_type),users(display_name)',
                eq: { is_published: true, is_public: true },
                order: 'published_at.desc',
                limit: 200  // Fetch more, then filter and sort
            });

            if (error) {
                console.error('Error searching articles:', error);
                return [];
            }

            // Filter for tag overlap (array containment)
            const results = (articles || []).filter(article => {
                if (!article.article_tags || article.article_tags.length === 0) return false;
                return tagArray.some(tag => article.article_tags.includes(tag));
            });

            // Sort by relevance (number of matching tags descending)
            results.sort((a, b) => {
                const aMatches = tagArray.filter(tag => a.article_tags.includes(tag)).length;
                const bMatches = tagArray.filter(tag => b.article_tags.includes(tag)).length;
                return bMatches - aMatches;  // Most matches first
            });

            // Add author_name and group_name to results
            const enriched = results.map(article => ({
                ...article,
                author_name: article.users?.display_name || 'Unknown Author',
                group_name: article.communities?.name || null,
                group_type: article.communities?.community_type || null
            }));

            const finalResults = enriched.slice(0, limit);
            console.log('✅ searchArticlesByTags: Found', finalResults.length, 'articles');
            return finalResults;
        } catch (error) {
            console.error('Exception in searchArticlesByTags:', error);
            return [];
        }
    },

    // Full-text search articles via search_articles RPC
    // Uses Postgres websearch_to_tsquery — supports quoted phrases, OR, minus
    async searchArticles(query, options = {}) {
        const { boundaryIds = null, communityId = null, articleType = null, limit = 30 } = options;

        if (!query || !query.trim()) {
            return [];
        }

        try {
            const rpcParams = { p_query: query.trim() };
            if (boundaryIds && boundaryIds.length > 0) {
                rpcParams.p_boundary_ids = boundaryIds;
            }
            if (communityId) {
                rpcParams.p_community_id = communityId;
            }
            if (articleType) {
                rpcParams.p_article_type = articleType;
            }
            if (limit !== 30) {
                rpcParams.p_limit = limit;
            }

            const { data, error } = await supabaseFetch.rpc('search_articles', rpcParams);

            if (error) {
                console.error('Error in searchArticles RPC:', error);
                return [];
            }

            return (data || []).map(article => ({
                id: article.id,
                title: article.title,
                content: article.content,
                summary: article.summary,
                article_type: article.article_type,
                cover_image_url: article.cover_image_url,
                hero_image_thumbnail_url: article.hero_image_thumbnail_url || null,
                video_url: article.video_url || null,
                author_id: article.author_id,
                author_name: article.author_name || 'Unknown Author',
                community_id: article.community_id,
                group_name: article.group_name || null,
                location_postcode: article.location_postcode,
                latitude: article.latitude,
                longitude: article.longitude,
                reputation_score: article.reputation_score || 0,
                view_count: article.view_count || 0,
                like_count: article.like_count || 0,
                comment_count: article.comment_count || 0,
                published_at: article.published_at,
                created_at: article.created_at
            }));
        } catch (error) {
            console.error('Exception in searchArticles:', error);
            return [];
        }
    }
};

// Event Service - Handles event-specific articles
export const eventService = {
    // Get locality-based events (for locality mode, using user's home area)
    // MIGRATION (March 2026): Replaced legacy locality+Haversine with boundary-based RPC
    // Options: { boundaryIds: BIGINT[], guestCoords: { lat, lng }, futureLimit: number, pastLimit: number }
    // Each window shows its own limit (default 50); Load More re-asks with one raised by 50 (Roadmap
    // #104 addendum 5). The no-boundary fallback has one mixed list and takes the larger of the two.
    async getLocalityEvents(userHomeAreaId = null, options = {}) {
        const { boundaryIds: optBoundaryIds = null, guestCoords = null, futureLimit = 50, pastLimit = 50 } = options;
        const EVENTS_FETCH_LIMIT = Math.max(futureLimit, pastLimit);

        try {
            // Get boundary_ids from user_areas table
            // NOTE: Events are stored in articles table with article_type='event'
            let boundaryIds = [];

            if (optBoundaryIds && optBoundaryIds.length > 0) {
                // Caller already resolved boundary IDs (e.g. guest with cached detection)
                boundaryIds = optBoundaryIds;
                console.log('🗺️ Loading events for', boundaryIds.length, 'pre-resolved boundaries');
            } else if (userHomeAreaId) {
                console.log('🔧 Loading locality events for home area ID:', userHomeAreaId);

                // Get boundary_ids from user_areas
                const { data: userArea, error: areaError } = await supabaseFetch.query('user_areas', {
                    select: 'boundary_ids',
                    eq: { id: userHomeAreaId },
                    single: true
                });

                if (areaError) {
                    console.error('Error fetching user area for events:', areaError);
                } else if (userArea?.boundary_ids && userArea.boundary_ids.length > 0) {
                    boundaryIds = userArea.boundary_ids;
                    console.log(`📍 User area has ${boundaryIds.length} boundaries`);
                } else {
                    console.warn('⚠️ User area has no boundary_ids');
                }
            } else if (guestCoords && guestCoords.lat && guestCoords.lng) {
                // Guest IP-detected coordinates — resolve to boundary
                console.log('🌍 Loading events for IP-detected coordinates:', guestCoords.lat, guestCoords.lng);
                try {
                    const { data: boundaryId, error: resolveError } = await supabaseFetch.rpc('resolve_point_to_boundary', {
                        p_lat: guestCoords.lat,
                        p_lng: guestCoords.lng
                    });
                    if (!resolveError && boundaryId) {
                        boundaryIds = [boundaryId];
                        console.log(`🌍 Resolved guest coords to boundary ID: ${boundaryId}`);
                    } else if (resolveError) {
                        console.warn('⚠️ resolve_point_to_boundary failed for events:', resolveError.message);
                    }
                } catch (err) {
                    console.warn('⚠️ Guest IP event resolution failed:', err.message);
                }
            }

            // If we have boundary_ids, request both Future and Past windows in parallel — each
            // correctly ordered and limited by the RPC itself (docs/briefs/2026-08-13-events-
            // ordering-by-start-time.md + addendum-1 §A3), not one mixed-by-publish-date fetch
            // re-split client-side. Both errors are checked: a failed 'past' call should not
            // silently blank the whole roster if 'future' succeeded, or vice versa.
            if (boundaryIds.length > 0) {
                // One row beyond the display limit per window — Future and Past are independent
                // windows (each its own toggle state in EventsColumn), so each needs its own
                // has-more answer, not one combined for the pair. Same withHasMore mechanism as
                // News; sliced back to EVENTS_FETCH_LIMIT before either window's data is used
                // for anything downstream, so a discarded 51st row never reaches event_details,
                // the roster, or any count.
                const [futureResult, pastResult] = await Promise.all([
                    supabaseFetch.rpc('get_articles_by_locality_v2', {
                        p_saved_ids: boundaryIds,
                        p_limit: futureLimit + 1,
                        p_article_types: ['event'],
                        p_event_window: 'future'
                    }),
                    supabaseFetch.rpc('get_articles_by_locality_v2', {
                        p_saved_ids: boundaryIds,
                        p_limit: pastLimit + 1,
                        p_article_types: ['event'],
                        p_event_window: 'past'
                    })
                ]);

                if (futureResult.error) {
                    console.error('Error fetching future events via RPC:', futureResult.error);
                }
                if (pastResult.error) {
                    console.error('Error fetching past events via RPC:', pastResult.error);
                }
                if (futureResult.error && pastResult.error) {
                    return [];
                }

                const futureRows = withHasMore(futureResult.data || [], futureLimit);
                const pastRows = withHasMore(pastResult.data || [], pastLimit);
                const hasMoreFuture = futureRows.hasMore;
                const hasMorePast = pastRows.hasMore;

                const articles = [...futureRows, ...pastRows];
                const events = articles.filter(a => a.article_type === 'event');

                if (events.length === 0) {
                    console.log(
                        futureResult.error || pastResult.error
                            ? '📍 No events returned — at least one window failed, see errors above'
                            : '📍 No events found within user boundaries'
                    );
                    return [];
                }

                console.log(`✅ Found ${events.length} events within user boundaries (more future: ${hasMoreFuture}, more past: ${hasMorePast})`);

                // Fetch event_details for all events in one query
                const eventIds = events.map(e => e.id);
                let detailsMap = {};
                try {
                    const { data: detailsData, error: detailsError } = await supabaseFetch.query('event_details', {
                        select: 'article_id,start_date,end_date,display_from,is_all_day,venue_name,venue_address,is_online,meeting_url,price_info',
                        inFilter: { article_id: eventIds }
                    });
                    if (!detailsError && detailsData) {
                        for (const d of detailsData) {
                            detailsMap[d.article_id] = d;
                        }
                    }
                } catch (err) {
                    console.warn('⚠️ event_details fetch failed, dates will be null:', err.message);
                }

                const mappedEvents = events.map(event => {
                    const details = detailsMap[event.id];
                    return {
                        id: event.id,
                        title: event.title,
                        content: event.content,
                        summary: event.summary,
                        article_type: event.article_type,
                        cover_image_url: event.cover_image_url,
                        hero_image_thumbnail_url: event.hero_image_thumbnail_url || null,
                        author_id: event.author_id,
                        author_name: event.author_name || 'Event Organizer',
                        community_id: event.community_id,
                        group_name: event.group_name || 'Community Event',
                        nearest_locality: Array.isArray(event.matching_locality_names) && event.matching_locality_names.length > 0
                            ? event.matching_locality_names.join(', ')
                            : null,
                        start_date: details?.start_date || null,
                        end_date: details?.end_date || null,
                        display_from: details?.display_from || null,
                        is_all_day: details?.is_all_day || false,
                        venue_name: details?.venue_name || null,
                        venue_address: details?.venue_address || null,
                        is_online: details?.is_online || false,
                        meeting_url: details?.meeting_url || null,
                        price_info: details?.price_info || null,
                        max_attendees: null,
                        current_attendees: 0,
                        view_count: event.view_count || 0,
                        like_count: event.like_count || 0,
                        comment_count: event.comment_count || 0,
                        published_at: event.published_at,
                        created_at: event.created_at
                    };
                });
                mappedEvents.hasMoreFuture = hasMoreFuture;
                mappedEvents.hasMorePast = hasMorePast;
                // Plain `hasMore` too, alongside the window-specific pair — so a consumer that
                // doesn't care about the Future/Past split (or hits the single-window fallback
                // below, which has no split to report) can check one consistent name either way.
                mappedEvents.hasMore = hasMoreFuture || hasMorePast;
                return mappedEvents;
            }

            // FALLBACK: No boundary_ids - return all public events. Single window (no
            // future/past split here), so one has-more flag per the same withHasMore mechanism —
            // sliced immediately so a discarded 51st row never reaches the event_details query.
            //
            // Group content follows the group (follow-ups addendum 1, Part 1, ruled 27 Sep 2026):
            // non-group events as before; a group event is left out when it has no locality (unless
            // platform content) or its group is soft-deleted. Two queries, because the group filter
            // needs the communities!inner embed (see the posts fallback in getLocalityNews for why),
            // which would drop non-group events. The top N+1 of the union is within the union of
            // each query's top N+1, so the merged has-more flag holds; ties break on id so the
            // order is stable between loads.
            console.warn('⚠️ No boundary_ids available - falling back to all public events');
            const fallbackSelect = 'id,title,content,summary,cover_image_url,author_id,community_id,created_at';
            const [nonGroup, grouped] = await Promise.all([
                supabaseFetch.query('articles', {
                    select: fallbackSelect,
                    eq: { article_type: 'event', is_public: true, is_published: true },
                    is: { community_id: null },
                    order: 'created_at.desc',
                    limit: EVENTS_FETCH_LIMIT + 1
                }),
                supabaseFetch.query('articles', {
                    select: `${fallbackSelect},communities!inner(deleted_at)`,
                    eq: { article_type: 'event', is_public: true, is_published: true },
                    is: { 'communities.deleted_at': null },
                    notNull: ['community_id'],
                    or: 'home_locality_id.not.is.null,is_platform_content.is.true',
                    order: 'created_at.desc',
                    limit: EVENTS_FETCH_LIMIT + 1
                })
            ]);

            const error = nonGroup.error || grouped.error;
            if (error) {
                console.error('Error fetching fallback events:', error);
                return [];
            }

            const fallbackData = [...(nonGroup.data || []), ...(grouped.data || [])]
                .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '') || String(b.id).localeCompare(String(a.id)))
                .slice(0, EVENTS_FETCH_LIMIT + 1);

            const data = withHasMore(fallbackData, EVENTS_FETCH_LIMIT);

            // Fetch event_details for fallback events
            const fallbackIds = (data || []).map(e => e.id);
            let fallbackDetailsMap = {};
            if (fallbackIds.length > 0) {
                try {
                    const { data: detailsData, error: detailsError } = await supabaseFetch.query('event_details', {
                        select: 'article_id,start_date,end_date,display_from,is_all_day,venue_name,venue_address,is_online,meeting_url,price_info',
                        inFilter: { article_id: fallbackIds }
                    });
                    if (!detailsError && detailsData) {
                        for (const d of detailsData) {
                            fallbackDetailsMap[d.article_id] = d;
                        }
                    }
                } catch (err) {
                    console.warn('⚠️ Fallback event_details fetch failed:', err.message);
                }
            }

            const mappedFallbackEvents = (data || []).map(event => {
                const details = fallbackDetailsMap[event.id];
                return {
                    id: event.id,
                    title: event.title,
                    content: event.content,
                    summary: event.summary,
                    article_type: 'event',
                    cover_image_url: event.cover_image_url,
                    author_id: event.author_id,
                    author_name: 'Event Organizer',
                    community_id: event.community_id,
                    group_name: 'Community Event',
                    nearest_locality: null,
                    start_date: details?.start_date || null,
                    end_date: details?.end_date || null,
                    display_from: details?.display_from || null,
                    is_all_day: details?.is_all_day || false,
                    venue_name: details?.venue_name || null,
                    venue_address: details?.venue_address || null,
                    is_online: details?.is_online || false,
                    meeting_url: details?.meeting_url || null,
                    price_info: details?.price_info || null,
                    max_attendees: null,
                    current_attendees: 0,
                    created_at: event.created_at
                };
            });
            mappedFallbackEvents.hasMore = data.hasMore;
            return mappedFallbackEvents;
        } catch (error) {
            console.error('Exception in getLocalityEvents:', error);
            return [];
        }
    },

    async getEvents(communityId) {
        try {
            console.log('🔧 Loading events for community:', communityId || 'all');

            // Shared shape for both branches below — identical either way, only the query differs.
            const mapEvent = (event) => ({
                id: event.id,
                title: event.title,
                content: event.content,
                summary: event.summary,
                article_type: 'event', // Carry the type — the front-page filter rejects events without it.
                cover_image_url: event.cover_image_url,
                author_name: 'Event Organizer', // Simplified - no join with supabaseFetch
                group_name: 'Community Event', // Simplified - no join with supabaseFetch
                start_date: null, // Would need separate query for event_details
                end_date: null,
                venue_name: null,
                venue_address: null,
                max_attendees: null,
                current_attendees: 0,
                view_count: event.view_count || 0,
                created_at: event.created_at
            });

            if (communityId) {
                // Ordered by start time, not created_at (docs/briefs/2026-08-13-events-ordering-
                // by-start-time.md + addendum-1 §A2). This path has no LIMIT, so no starvation to
                // fix — only the ordering axis. EventsColumn/GroupEventsColumn already re-sort
                // within each Future/Past bucket client-side regardless of incoming order
                // (addendum §A3), so one chronological call is enough — no future/past split
                // needed here, unlike the locality path. Note this RPC's own ORDER BY currently
                // has no observable effect on any surface: GroupFrontPage.jsx/
                // MobileGroupFrontPage.jsx already re-sort by start_date before the column ever
                // sees the data. This is a correctness/architecture fix at the source, not a
                // user-visible change today — don't go looking for a display difference.
                // Demo read target on → the client-side mirror (the public function cannot see demo rows).
                const { data, error } = isDemoReadActive()
                    ? await demoGetGroupEventsByStartTime(communityId)
                    : await supabaseFetch.rpc('get_group_events_by_start_time', {
                        p_community_id: communityId
                    });

                console.log('🔧 Group events query result:', { dataCount: data?.length, error });

                if (error) {
                    console.error('Error fetching group events via RPC:', error);
                    return [];
                }

                return (data || []).map(mapEvent);
            }

            // No communityId: public events across all groups. Not reached by any live caller
            // today (both App.js call sites always pass a group id) — left on the original raw
            // query. get_group_events_by_start_time requires p_community_id and has no
            // "all groups" mode, so it isn't a drop-in replacement for this branch.
            const { data, error } = await supabaseFetch.query('articles', {
                select: 'id,title,content,summary,cover_image_url,author_id,community_id,view_count,created_at',
                eq: { article_type: 'event', is_published: true, is_public: true },
                order: 'created_at.desc'
            });

            console.log('🔧 Events query result:', { dataCount: data?.length, error });

            if (error) {
                console.error('Error fetching events:', error);
                return [];
            }

            return (data || []).map(mapEvent);
        } catch (error) {
            console.error('Exception in getEvents:', error);
            return [];
        }
    },

    async getEventById(articleId) {
        try {
            // Use supabaseFetch to avoid JS client hanging on nested joins
            // Fetch article first
            const { data: articleData, error: articleError } = await supabaseFetch.query('articles', {
                select: '*',
                eq: { id: articleId, article_type: 'event' },
                single: true
            });

            if (articleError || !articleData) {
                console.error('Error fetching event article:', articleError);
                return null;
            }

            // Fetch event_details separately
            const { data: eventDetails, error: detailsError } = await supabaseFetch.query('event_details', {
                select: 'start_date,end_date,display_from,venue_name,venue_address,venue_postcode,max_attendees,current_attendees,contact_phone,contact_email',
                eq: { article_id: articleId },
                single: true
            });

            if (detailsError) {
                console.warn('Error fetching event details:', detailsError);
            }

            // Fetch community info if community_id exists
            let communityData = null;
            if (articleData.community_id) {
                const { data: community } = await supabaseFetch.query('communities', {
                    select: 'name,community_type,logo_url',
                    eq: { id: articleData.community_id },
                    single: true
                });
                communityData = community;
            }

            // Fetch author info if author_id exists
            let authorData = null;
            if (articleData.author_id) {
                const { data: profiles } = await getPublicProfiles([articleData.author_id]);
                authorData = profiles?.[0] || null;
            }

            return {
                ...articleData,
                author_name: authorData?.display_name || 'Unknown Author',
                group_name: communityData?.name || 'Unknown Group',
                group_logo_url: communityData?.logo_url || null,
                group_type: communityData?.community_type,
                start_date: eventDetails?.start_date,
                end_date: eventDetails?.end_date,
                display_from: eventDetails?.display_from || null,
                venue_name: eventDetails?.venue_name,
                venue_address: eventDetails?.venue_address,
                venue_postcode: eventDetails?.venue_postcode,
                max_attendees: eventDetails?.max_attendees,
                current_attendees: eventDetails?.current_attendees || 0,
                contact_phone: eventDetails?.contact_phone,
                contact_email: eventDetails?.contact_email
            };
        } catch (error) {
            console.error('Exception in getEventById:', error);
            return null;
        }
    },

    // Ratified tag vocabulary (tags foundation, Aug 2026) — active parent categories, each
    // with its active child terms nested, both ordered by display_order. Two plain queries
    // joined in JS (no nested joins, per Architecture.md § Critical Data Access Rules).
    // Consumed by AddEditEvent's tag-picker; a general-purpose read, not event-specific,
    // kept here since eventService is this build's only consumer so far.
    async getTagVocabulary() {
        try {
            const { data: categories, error: catError } = await supabaseFetch.query('tag_categories', {
                select: 'id,display_order,i18n_key',
                eq: { is_active: true },
                order: 'display_order.asc'
            });
            if (catError) {
                console.error('Error loading tag_categories:', catError);
                return [];
            }

            const { data: terms, error: termError } = await supabaseFetch.query('tag_terms', {
                select: 'id,category_id,display_order,i18n_key,image_url',
                eq: { is_active: true },
                order: 'display_order.asc'
            });
            if (termError) {
                console.error('Error loading tag_terms:', termError);
                return [];
            }

            return (categories || []).map(category => ({
                ...category,
                terms: (terms || []).filter(term => term.category_id === category.id)
            }));
        } catch (error) {
            console.error('Exception in getTagVocabulary:', error);
            return [];
        }
    },

    // R4 house fallback (image-layer build, 11 Aug 2026) — the one global
    // configurable value in platform_settings (report B6 precedent:
    // LocationDepthAdmin.jsx's identical read shape). Returns null (not '')
    // on any failure so a null-coalescing caller degrades safely rather than
    // writing/rendering an empty-string image src.
    async getTagHouseFallbackImageUrl() {
        try {
            const { data, error } = await supabaseFetch.query('platform_settings', {
                select: 'value',
                eq: { key: 'tag_house_fallback_image_url' },
                single: true
            });
            if (error || !data) {
                console.error('Error loading tag_house_fallback_image_url:', error);
                return null;
            }
            return data.value || null;
        } catch (error) {
            console.error('Exception in getTagHouseFallbackImageUrl:', error);
            return null;
        }
    },

    // is_public fix (docs/briefs/2026-08-15-fix-event-is-public-default.md, R1) — the single
    // column AddEditEvent needs to derive its is_public form field from the selected group's
    // privacy. Returns null (not a boolean) on any failure, so a caller deriving is_public from
    // this can distinguish "unresolvable, leave the form field as it was" from a real false.
    async getCommunityPrivacy(communityId) {
        try {
            const { data, error } = await supabaseFetch.query('communities', {
                select: 'is_private',
                eq: { id: communityId },
                single: true
            });
            if (error || !data) {
                console.error('Error loading community privacy for', communityId, error);
                return null;
            }
            return data.is_private;
        } catch (error) {
            console.error('Exception in getCommunityPrivacy:', error);
            return null;
        }
    }
};

// User Service - Authentication and profile management
export const userService = {
    async getCurrentUser() {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            return user;
        } catch (error) {
            console.error('Error getting current user:', error);
            return null;
        }
    },

    async signIn(email, password) {
        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password
            });

            if (error) {
                console.error('Sign in error:', error);
                throw error;
            }

            return data;
        } catch (error) {
            console.error('Exception in signIn:', error);
            throw error;
        }
    },

    async signOut() {
        try {
            const { error } = await supabase.auth.signOut();
            if (error) {
                console.error('Sign out error:', error);
                throw error;
            }
        } catch (error) {
            console.error('Exception in signOut:', error);
            throw error;
        }
    }
};

// Export functions that match existing V07 imports
export const getOriginalChats = async (communityId) => {
    return await chatService.getOriginalChats(communityId);
};

export const getThreadChats = async (originalChatId) => {
    return await chatService.getThreadChats(originalChatId);
};

export const getData = {
    localityNews: async (postcode) => await articleService.getLocalityNews(postcode),
    article: async (articleId) => await articleService.getArticleById(articleId)
};

// ============================================
// GROUP SERVICE - V10 Addition
// ============================================

// member_count after a join/leave. On a demo page the public RPCs cannot reach demo.communities
// (an rpc() always runs on public), so the client-side twin does the same update there.
const adjustMemberCount = (groupId, delta) => (isDemoReadActive()
    ? demoAdjustMemberCount(groupId, delta)
    : supabaseFetch.rpc(delta > 0 ? 'increment_member_count' : 'decrement_member_count', { community_id: groupId }));

// An owner-block denies the INSERT at the RLS layer (both the group_join_requests gate and
// the auto-accept community_members gate carry the same NOT EXISTS block-check —
// 20260906b_owner_block_person_from_group.sql). Postgres reports any WITH CHECK failure
// identically ("new row violates row-level security policy..."), so this can't distinguish a
// block from a hypothetical auth.uid()-mismatch on the same policy — narrower than a bare 403
// check, but not perfectly precise; that residual edge case (e.g. a stale session) would also
// read as "blocked" rather than a session error. Matches this codebase's existing habit of
// flagging known-imprecise error mapping (see joinGroup's rate-limit check).
const isBlockedByRls = (error) =>
    error?.status === 403 && error?.message?.includes('policy');

export const groupService = {
    // Get groups user has joined
    // Uses supabaseFetch with separate queries (supabase client with joins can hang)
    async getUserGroups(userId) {
        try {
            console.log('📡 getUserGroups: Loading for user:', userId);

            // Step 1: Get community memberships
            const { data: memberships, error: memberError } = await supabaseFetch.query('community_members', {
                select: 'community_id',
                eq: { user_id: userId }
            });

            if (memberError) {
                console.error('Error loading memberships:', memberError);
                return [];
            }

            if (!memberships || memberships.length === 0) {
                console.log('📡 getUserGroups: No memberships found');
                return [];
            }

            // Step 2: Get community details for all joined communities. No demo filter: demo groups
            // live in the `demo` schema and `public` cannot hold one (20260918d CHECK), so none can
            // reach a real user's "my groups" view.
            const communityIds = memberships.map(m => m.community_id);
            const { data: communities, error: commError } = await supabaseFetch.query('communities', {
                select: 'id, name, description, logo_url, image_caption, postcode, member_count, article_count, is_locality_linked, cover_image_url, owner_id, is_private, auto_accept_members, group_access, deleted_at, deletion_scheduled_at',
                inFilter: { id: communityIds }
            });

            if (commError) {
                console.error('Error loading communities:', commError);
                return [];
            }

            // Step 3: Get owner display names
            const ownerIds = [...new Set((communities || []).map(c => c.owner_id).filter(Boolean))];
            let ownerMap = {};
            if (ownerIds.length > 0) {
                const { data: owners } = await supabaseFetch.rpc('get_public_profiles', {
                    p_user_ids: ownerIds
                });
                ownerMap = (owners || []).reduce((acc, u) => ({ ...acc, [u.id]: u.display_name }), {});
            }

            console.log('📡 getUserGroups: Found', communities?.length, 'groups');

            return (communities || []).map(c => ({
                id: c.id,
                name: c.name,
                description: c.description,
                logo_url: c.logo_url,
                postcode: c.postcode,
                member_count: c.member_count,
                article_count: c.article_count,
                is_locality_linked: c.is_locality_linked,
                owner_id: c.owner_id,
                owner_display_name: ownerMap[c.owner_id] || 'Unknown',
                is_private: c.is_private,
                auto_accept_members: c.auto_accept_members,
                group_access: c.group_access,
                distance: null // TODO: Calculate distance when we have PostGIS
            }));
        } catch (error) {
            console.error('Exception in getUserGroups:', error);
            return [];
        }
    },

    // Resolve the locality a group's content takes (#72: group content follows the group —
    // docs/briefs/2026-09-27-unlocated-group-events-stay-in-group.md, addenda 1-3).
    // Shared by AddEditArticle, AddEditEvent and WalkingComposer — single source of truth for
    // "which boundary does this group's content anchor to".
    // Returns { status, homeLocalityId, boundaryName, boundaries }:
    //   'located'   — homeLocalityId is the group's first boundary in boundary_ids order that
    //                 exists in admin_boundaries; boundaries is the named list when there is more
    //                 than one (the default first, the rest by name) for the limited picker.
    //   'unlocated' — the group has no boundary: its content gets no locality and stays in the group.
    //   'error'     — the lookup failed, or the group's boundaries do not resolve: callers block
    //                 saving rather than guess, so a transient failure never unlocates content.
    // { firstOnly: true } resolves just the default (the walking composer offers no picker).
    async resolveGroupHomeLocality(groupId, { firstOnly = false } = {}) {
        const none = { homeLocalityId: null, boundaryName: null, boundaries: [] };
        if (!groupId) return { status: 'error', ...none };
        try {
            const { data: community, error } = await supabaseFetch.query('communities', {
                select: 'boundary_ids',
                eq: { id: groupId },
                single: true
            });
            if (error || !community) {
                console.warn('resolveGroupHomeLocality: group lookup failed:', error);
                return { status: 'error', ...none };
            }
            const bids = community.boundary_ids || [];
            if (!bids.length) return { status: 'unlocated', ...none };

            const { data: named, error: namesError } =
                await getBoundaryNamesInOrder(firstOnly ? bids.slice(0, 200) : bids);
            if (namesError || !named.length) {
                console.warn('resolveGroupHomeLocality: boundary names did not resolve:', namesError);
                return { status: 'error', ...none };
            }
            const [first, ...rest] = named;
            return {
                status: 'located',
                homeLocalityId: first.id,
                boundaryName: first.name,
                boundaries: named.length > 1
                    ? [first, ...rest.sort((a, b) => (a.name || '').localeCompare(b.name || ''))]
                    : []
            };
        } catch (err) {
            console.warn('resolveGroupHomeLocality: lookup failed:', err);
            return { status: 'error', ...none };
        }
    },

    // Group content's map point (docs/briefs/2026-09-27-group-build-follow-ups.md Part 4): the
    // centroid of the one boundary the content is placed in (its home_locality_id). Callers pass
    // null for content with no locality (an unlocated group) and store no point. Returns
    // { lat, lng } or null; a failed lookup also returns null — location_point is informational
    // only (Architecture.md § location_point Is Not a Geographic Fact).
    async getBoundaryCentroid(boundaryId) {
        if (!boundaryId) return null;
        try {
            const { data, error } = await supabaseFetch.rpc('get_smallest_boundary_centroid', {
                p_boundary_ids: [Number(boundaryId)]
            });
            if (error || !data?.length) {
                console.warn('getBoundaryCentroid: no centroid for', boundaryId, error);
                return null;
            }
            return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lng) };
        } catch (err) {
            console.warn('getBoundaryCentroid: lookup failed:', err);
            return null;
        }
    },

    // Get groups owned by user (includes hierarchy fields for nested groups)
    // Uses supabaseFetch (supabase client can hang)
    async getOwnedGroups(userId) {
        try {
            console.log('📡 getOwnedGroups: Loading for user:', userId);

            // No demo filter — same reasoning as getUserGroups above (demo groups are not in
            // `public`). NOTE this function has a third consumer beyond the Groups page:
            // StewardWorkspace.jsx's handleLinkExisting directory-listing picker also calls it.
            const { data, error } = await supabaseFetch.query('communities', {
                select: 'id, name, description, logo_url, image_caption, postcode, member_count, article_count, is_private, auto_accept_members, group_access, cover_image_url, parent_group_id, hierarchy_level, visible_to_parent, owner_id, community_type, deleted_at, deletion_scheduled_at',
                eq: { owner_id: userId }
            });

            if (error) {
                console.error('Error loading owned groups:', error);
                return [];
            }

            // Filter to groups only and sort by hierarchy_level then name
            const groups = (data || [])
                .filter(c => c.community_type === 'group')
                .sort((a, b) => {
                    const levelDiff = (a.hierarchy_level || 0) - (b.hierarchy_level || 0);
                    if (levelDiff !== 0) return levelDiff;
                    return (a.name || '').localeCompare(b.name || '');
                });

            // Enrich with owner display names
            const ownerIds = [...new Set(groups.map(g => g.owner_id).filter(Boolean))];
            let ownerMap = {};
            if (ownerIds.length > 0) {
                const { data: owners } = await supabaseFetch.rpc('get_public_profiles', {
                    p_user_ids: ownerIds
                });
                ownerMap = (owners || []).reduce((acc, u) => ({ ...acc, [u.id]: u.display_name }), {});
            }

            console.log('📡 getOwnedGroups: Found', groups.length, 'groups');
            return groups.map(g => ({ ...g, owner_display_name: ownerMap[g.owner_id] || null }));
        } catch (error) {
            console.error('Exception in getOwnedGroups:', error);
            return [];
        }
    },

    // Search groups by tags, name, description, and/or location
    // Uses supabaseFetch with separate queries (supabase client can hang)
    async searchGroups({ tags, location, currentUserLocation }) {
        try {
            console.log('📡 searchGroups: Searching with tags:', tags, 'location:', location);

            // Step 1: Get tag-matched community IDs if tags provided
            let tagFilteredIds = null;
            if (tags && tags.trim()) {
                const tagArray = tags.split(',').map(t => t.trim());
                const { data: tagMatches, error: tagError } = await supabaseFetch.query('community_tags', {
                    select: 'community_id, tag'
                });

                if (tagError) {
                    console.error('Error fetching tags:', tagError);
                    // Don't return empty - continue to name-based search
                }

                // Filter to matching tags
                const matchingTags = (tagMatches || []).filter(t =>
                    tagArray.some(tag => t.tag?.toLowerCase() === tag.toLowerCase())
                );

                if (matchingTags.length > 0) {
                    tagFilteredIds = [...new Set(matchingTags.map(t => t.community_id))];
                    console.log('📡 searchGroups: Found', tagFilteredIds.length, 'tag matches');
                } else {
                    console.log('📡 searchGroups: No tag matches found, will search by name/description');
                }
            }

            // Step 2: Fetch all public groups (we'll filter in JS). Discovery can never surface demo
            // content: demo groups live in the `demo` schema, not `public` (20260918d CHECK).
            const { data: allGroups, error: groupError } = await supabaseFetch.query('communities', {
                select: 'id, name, description, logo_url, image_caption, postcode, member_count, article_count, cover_image_url, owner_id, is_private, auto_accept_members, group_access, community_type, parent_group_id',
                limit: 200 // Fetch more, then filter
            });

            if (groupError) {
                console.error('Error searching groups:', groupError);
                return [];
            }

            console.log('📡 searchGroups: Fetched', allGroups?.length || 0, 'total groups from database');

            // Step 3: Filter results
            let results = (allGroups || []).filter(g => {
                // Must be a group
                if (g.community_type !== 'group') return false;
                // Must be findable: public or private_findable (addendum 5). Fail-closed: a row
                // without its type is not shown.
                if (g.group_access !== 'public' && g.group_access !== 'private_findable') return false;
                // Must be top-level (no parent) - sub-groups discovered inside parent feed
                if (g.parent_group_id) return false;

                // If tags provided, check if matches tags OR name/description
                if (tags && tags.trim()) {
                    const searchTerms = tags.split(',').map(t => t.trim().toLowerCase());
                    const matchesTag = tagFilteredIds && tagFilteredIds.includes(g.id);
                    const matchesName = searchTerms.some(term =>
                        g.name?.toLowerCase().includes(term) ||
                        g.description?.toLowerCase().includes(term)
                    );
                    const matched = matchesTag || matchesName;
                    if (!matched) {
                        console.log('📡 Group filtered out:', g.name, '- No tag or name match for:', searchTerms);
                    }
                    return matched;
                }

                // If location filter, postcode must contain the search term (case-insensitive)
                if (location && location.trim()) {
                    const searchTerm = location.trim().toLowerCase();
                    if (!g.postcode || !g.postcode.toLowerCase().includes(searchTerm)) return false;
                }
                return true;
            });

            // Step 4: Get owner display names
            const ownerIds = [...new Set(results.map(g => g.owner_id).filter(Boolean))];
            let ownerMap = {};
            if (ownerIds.length > 0) {
                const { data: owners } = await supabaseFetch.rpc('get_public_profiles', {
                    p_user_ids: ownerIds
                });
                ownerMap = (owners || []).reduce((acc, u) => ({ ...acc, [u.id]: u.display_name }), {});
            }

            // Step 5: Limit and map results
            results = results.slice(0, 50).map(group => ({
                ...group,
                owner_display_name: ownerMap[group.owner_id] || 'Unknown'
            }));

            console.log('📡 searchGroups: Found', results.length, 'groups');
            return results;
        } catch (error) {
            console.error('Exception in searchGroups:', error);
            return [];
        }
    },

    // P2 reverse-reach: Discover feed via get_groups_by_reverse_reach RPC — replaces the
    // unordered searchGroups({}) path (probe Q1). boundaryIds is the searcher's context
    // boundary set, sourced by the caller — not derived here — so this stays a plain RPC
    // wrapper agnostic to where that context comes from. Today it's the single currently
    // selected Area (see useGroups.js); a resolved escalation (docs/escalations/2026-09-08-
    // p2-searcher-area-union-scope.md) ruled against unioning a user's Areas, grounded in
    // TRUE §920 — cross-Area unioning would mix what become separate personas' contexts once
    // Personas ship, so this deliberately takes just an array rather than a userId, leaving
    // that swap a call-site change, not a rewrite. An empty array (guest / no-Area) is the
    // RPC's documented graceful fallback: every discoverable group returned at score 0.
    async getGroupsByReverseReach(boundaryIds, options = {}) {
        const { limit = 50 } = options;
        try {
            const { data, error } = await supabaseFetch.rpc('get_groups_by_reverse_reach', {
                p_boundary_ids: boundaryIds || [],
                p_limit: limit
            });

            if (error) {
                console.error('Error in getGroupsByReverseReach RPC:', error);
                return [];
            }

            return data || [];
        } catch (error) {
            console.error('Exception in getGroupsByReverseReach:', error);
            return [];
        }
    },

    // Server-side group search via search_communities RPC (ILIKE on name/description)
    async searchCommunities(query, options = {}) {
        const { limit = 30 } = options;

        if (!query || !query.trim()) {
            return [];
        }

        try {
            const { data, error } = await supabaseFetch.rpc('search_communities', {
                p_query: query.trim(),
                p_limit: limit
            });

            if (error) {
                console.error('Error in searchCommunities RPC:', error);
                return [];
            }

            return (data || []).map(group => ({
                ...group,
                owner_display_name: group.owner_display_name || 'Unknown'
            }));
        } catch (error) {
            console.error('Exception in searchCommunities:', error);
            return [];
        }
    },

    // Leave a group
    async leaveGroup(userId, groupId) {
        try {
            // First get the membership to delete (supabaseFetch.delete needs single eq)
            const { data: membership, error: findError } = await supabaseFetch.query('community_members', {
                select: 'id',
                eq: { user_id: userId, community_id: groupId },
                single: true
            });

            if (findError || !membership) {
                console.error('Error finding membership:', findError);
                return false;
            }

            const { error } = await supabaseFetch.delete('community_members', {
                eq: { id: membership.id }
            });

            if (error) {
                console.error('Error leaving group:', error);
                return false;
            }

            // Decrement member count
            await adjustMemberCount(groupId, -1);

            return true;
        } catch (error) {
            console.error('Exception in leaveGroup:', error);
            return false;
        }
    },

    // Join a group (checks auto_accept_members setting)
    // Returns: { success: boolean, status: 'joined' | 'pending' | 'blocked' | 'error', message?: string }
    async joinGroup(userId, groupId, requestMessage = null) {
        try {
            // 1. Get group settings
            let { data: group, error: groupError } = await supabaseFetch.query('communities', {
                select: 'auto_accept_members,name',
                eq: { id: groupId },
                single: true
            });

            // A private group's row is for its members only, so a non-member of a findable private
            // group cannot read it — yet may ask to join (addendum 5 R1). get_group_introduction
            // answers for a findable group; both private types never admit anyone directly.
            if (groupError || !group) {
                const intro = await this.getGroupIntroduction(groupId);
                group = intro?.found && intro.accessType !== 'public'
                    ? { auto_accept_members: false, name: intro.name }
                    : null;
            }

            if (!group) {
                console.error('Error fetching group:', groupError);
                return { success: false, status: 'error', message: 'Group not found' };
            }

            // 2. Check if already a member
            const { data: existing } = await supabaseFetch.query('community_members', {
                select: 'id',
                eq: { user_id: userId, community_id: groupId },
                single: true
            });

            if (existing) {
                return { success: false, status: 'error', message: 'Already a member' };
            }

            // 3. Check if there's already a pending request (table may not exist yet)
            const { data: pendingRequest, error: pendingError } = await supabaseFetch.query('group_join_requests', {
                select: 'id,status',
                eq: { user_id: userId, community_id: groupId, status: 'pending' },
                single: true
            });

            // Ignore 404 errors (table doesn't exist yet)
            if (pendingRequest && !pendingError) {
                return { success: false, status: 'pending', message: 'Request already pending' };
            }

            // 4. If auto-accept, join immediately
            if (group.auto_accept_members) {
                const { error } = await supabaseFetch.insert('community_members', {
                    user_id: userId,
                    community_id: groupId,
                    role: 'member'
                });

                if (error) {
                    if (isBlockedByRls(error)) {
                        return { success: false, status: 'blocked', message: 'You are not able to request to join this group.' };
                    }
                    console.error('Error joining group:', error);
                    return { success: false, status: 'error', message: 'Failed to join group' };
                }

                // Increment member count
                await adjustMemberCount(groupId, 1);

                return { success: true, status: 'joined', groupName: group.name };
            }

            // 5. If requires approval, create join request (table may not exist yet)
            const { error: requestError } = await supabaseFetch.insert('group_join_requests', {
                community_id: groupId,
                user_id: userId,
                request_message: requestMessage,
                status: 'pending'
            });

            if (requestError) {
                // If table doesn't exist, fall back to auto-join
                if (requestError.status === 404) {
                    console.warn('group_join_requests table not found, falling back to auto-join');
                    const { error: fallbackError } = await supabaseFetch.insert('community_members', {
                        user_id: userId,
                        community_id: groupId,
                        role: 'member'
                    });
                    if (!fallbackError) {
                        await adjustMemberCount(groupId, 1);
                        return { success: true, status: 'joined', groupName: group.name };
                    }
                }
                if (isBlockedByRls(requestError)) {
                    return { success: false, status: 'blocked', message: 'You are not able to request to join this group.' };
                }
                console.error('Error creating join request:', requestError);
                // A rate-limit refusal goes back as its own status with the error, so the page can
                // say which limit and when to try again (rateLimitMessage; addendum 3 item 8).
                if (requestError?.message?.includes('rate_limit_exceeded')) {
                    return { success: false, status: 'rate_limited', error: requestError };
                }
                return { success: false, status: 'error', message: 'Failed to submit request' };
            }

            return { success: true, status: 'pending', groupName: group.name };
        } catch (error) {
            console.error('Exception in joinGroup:', error);
            return { success: false, status: 'error', message: 'An error occurred' };
        }
    },

    // Check if user is a member of a group
    async checkMembership(userId, groupId) {
        try {
            // Use supabaseFetch - JS client hangs on queries
            const { data, error } = await supabaseFetch.query('community_members', {
                select: 'id,role',
                eq: { user_id: userId, community_id: groupId },
                single: true
            });

            if (error) {
                console.error('Error checking membership:', error);
                return { isMember: false, role: null };
            }

            return { isMember: !!data, role: data?.role || null };
        } catch (error) {
            console.error('Exception in checkMembership:', error);
            return { isMember: false, role: null };
        }
    },

    // Get user's join request status for a group
    async getJoinRequestStatus(userId, groupId) {
        try {
            // Use supabaseFetch - JS client hangs on queries
            const { data, error } = await supabaseFetch.query('group_join_requests', {
                select: 'id,status,request_message,response_message,created_at,responded_at',
                eq: { user_id: userId, community_id: groupId },
                order: 'created_at.desc',
                limit: 1,
                single: true
            });

            if (error) {
                // Silently handle table not existing (404) - feature not yet deployed
                if (error.status === 404) {
                    return null;
                }
                console.error('Error getting join request status:', error);
                return null;
            }

            return data;
        } catch (error) {
            console.error('Exception in getJoinRequestStatus:', error);
            return null;
        }
    },

    // Cancel a pending join request
    async cancelJoinRequest(userId, groupId) {
        try {
            // Find the pending request first (supabaseFetch.delete needs single eq)
            const { data: request, error: findError } = await supabaseFetch.query('group_join_requests', {
                select: 'id',
                eq: { user_id: userId, community_id: groupId, status: 'pending' },
                single: true
            });

            // If table doesn't exist or no request found, return success
            if (findError?.status === 404 || !request) {
                return true;
            }

            const { error } = await supabaseFetch.delete('group_join_requests', {
                eq: { id: request.id }
            });

            if (error) {
                console.error('Error canceling join request:', error);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Exception in cancelJoinRequest:', error);
            return false;
        }
    },

    // Get pending join requests for a group (admin function).
    // request_kind/applicant_group_id/request_data added (Federation 4C §2) so a federation
    // request renders as one, not as a person's — probe A3's finding, fixed here.
    async getPendingJoinRequests(groupId) {
        try {
            // Get pending requests (table may not exist yet)
            const { data: requests, error } = await supabaseFetch.query('group_join_requests', {
                select: 'id,user_id,request_kind,applicant_group_id,request_message,request_data,created_at',
                eq: { community_id: groupId, status: 'pending' },
                order: 'created_at.asc'
            });

            // If table doesn't exist, return empty
            if (error?.status === 404) {
                return [];
            }

            if (error) {
                console.error('Error getting pending requests:', error);
                return [];
            }

            if (!requests || requests.length === 0) {
                return [];
            }

            // Fetch user details separately (avoiding nested joins that can hang)
            // Display name and avatar only — a join requester's email is a fact known
            // to OurNet, not to the group's owner (Architect ruling, 3 Aug 2026).
            const userIds = [...new Set(requests.map(r => r.user_id))];
            const { data: users } = await supabaseFetch.rpc('get_public_profiles', {
                p_user_ids: userIds
            });

            const userMap = (users || []).reduce((acc, u) => {
                acc[u.id] = u;
                return acc;
            }, {});

            // Federation-kind rows: the subject of the row is the APPLICANT group, not the
            // human who filed it (4C §2) — fetch its identity, and the federation's OWN
            // current application schema (fresh, not from a possibly-stale caller-held prop —
            // a Federator can reword/reorder/remove questions between filing and review, and
            // this list must label answers from what the schema says NOW).
            const applicantGroupIds = [...new Set(
                requests.filter(r => r.request_kind === 'federation' && r.applicant_group_id)
                    .map(r => r.applicant_group_id)
            )];

            let applicantGroupMap = {};
            let federationSchema = null;
            if (applicantGroupIds.length > 0) {
                const [{ data: applicantGroups }, { data: federationGroup }] = await Promise.all([
                    supabaseFetch.query('communities', {
                        select: 'id,name,logo_url',
                        inFilter: { id: applicantGroupIds }
                    }),
                    supabaseFetch.query('communities', {
                        select: 'federation_application_schema',
                        eq: { id: groupId },
                        single: true
                    })
                ]);
                applicantGroupMap = (applicantGroups || []).reduce((acc, g) => {
                    acc[g.id] = g;
                    return acc;
                }, {});
                federationSchema = federationGroup?.federation_application_schema || null;
            }

            // Combine requests with user data (and, for federation rows, applicant group +
            // the current schema to label request_data against).
            return requests.map(request => ({
                ...request,
                user: userMap[request.user_id] || null,
                applicant_group: request.request_kind === 'federation' && request.applicant_group_id
                    ? (applicantGroupMap[request.applicant_group_id] || null)
                    : null,
                federation_application_schema: request.request_kind === 'federation' ? federationSchema : null
            }));
        } catch (error) {
            console.error('Exception in getPendingJoinRequests:', error);
            return [];
        }
    },

    // Accept a join request (admin function)
    async acceptJoinRequest(requestId, responderId, responseMessage = null) {
        try {
            // Use the database function for atomic operation
            const { data, error } = await supabaseFetch.rpc('accept_join_request', {
                request_uuid: requestId,
                responder_uuid: responderId,
                response_msg: responseMessage
            });

            if (error) {
                console.error('Error accepting join request:', error);
                return false;
            }

            return data === true;
        } catch (error) {
            console.error('Exception in acceptJoinRequest:', error);
            return false;
        }
    },

    // Reject a join request (admin function)
    async rejectJoinRequest(requestId, responderId, responseMessage = null) {
        try {
            // Use the database function
            const { data, error } = await supabaseFetch.rpc('reject_join_request', {
                request_uuid: requestId,
                responder_uuid: responderId,
                response_msg: responseMessage
            });

            if (error) {
                console.error('Error rejecting join request:', error);
                return false;
            }

            return data === true;
        } catch (error) {
            console.error('Exception in rejectJoinRequest:', error);
            return false;
        }
    },

    // Block a person from requesting to join / auto-joining this group again (admin function).
    // Resolving any pending request and enforcing the block are both done at the DB
    // (20260906b_owner_block_person_from_group.sql's trigger + extended INSERT policies) —
    // this is just the insert. Returns true on success (including "already blocked" — the
    // table's UNIQUE(community_id, blocked_user_id) makes a re-block idempotent, not an error).
    async blockJoinRequester(communityId, blockedUserId, blockedBy) {
        try {
            const { error } = await supabaseFetch.insert('group_join_blocks', {
                community_id: communityId,
                blocked_user_id: blockedUserId,
                blocked_by: blockedBy
            });

            if (error && error.status !== 409) {
                console.error('Error blocking user:', error);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Exception in blockJoinRequester:', error);
            return false;
        }
    },

    // Unblock a person — after this, they may request to join again (subject to the normal
    // flood guard), with no lingering lockout (admin function).
    async unblockJoinRequester(communityId, blockedUserId) {
        try {
            // Find the block row first (supabaseFetch.delete needs single eq)
            const { data: block, error: findError } = await supabaseFetch.query('group_join_blocks', {
                select: 'id',
                eq: { community_id: communityId, blocked_user_id: blockedUserId },
                single: true
            });

            if (findError?.status === 404 || !block) {
                return true;
            }

            const { error } = await supabaseFetch.delete('group_join_blocks', {
                eq: { id: block.id }
            });

            if (error) {
                console.error('Error unblocking user:', error);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Exception in unblockJoinRequester:', error);
            return false;
        }
    },

    // Get the blocked-people list for a group (admin function). Display name and avatar only —
    // same get_public_profiles seam getPendingJoinRequests uses, no email ever.
    async getBlockedUsers(communityId) {
        try {
            const { data: blocks, error } = await supabaseFetch.query('group_join_blocks', {
                select: 'id,blocked_user_id,created_at',
                eq: { community_id: communityId },
                order: 'created_at.desc'
            });

            if (error) {
                console.error('Error getting blocked users:', error);
                return [];
            }

            if (!blocks || blocks.length === 0) {
                return [];
            }

            const userIds = [...new Set(blocks.map(b => b.blocked_user_id))];
            const { data: users } = await supabaseFetch.rpc('get_public_profiles', {
                p_user_ids: userIds
            });

            const userMap = (users || []).reduce((acc, u) => {
                acc[u.id] = u;
                return acc;
            }, {});

            return blocks.map(block => ({
                ...block,
                user: userMap[block.blocked_user_id] || null
            }));
        } catch (error) {
            console.error('Exception in getBlockedUsers:', error);
            return [];
        }
    },

    // Get pending requests count for a group
    async getPendingRequestsCount(groupId) {
        try {
            // supabaseFetch doesn't support count, so we fetch and count client-side
            const { data, error } = await supabaseFetch.query('group_join_requests', {
                select: 'id',
                eq: { community_id: groupId, status: 'pending' }
            });

            // If table doesn't exist, return 0
            if (error?.status === 404) {
                return 0;
            }

            if (error) {
                console.error('Error getting pending requests count:', error);
                return 0;
            }

            return data?.length || 0;
        } catch (error) {
            console.error('Exception in getPendingRequestsCount:', error);
            return 0;
        }
    },

    // Get groups where user is admin (for parent group selection in nested groups)
    async getAdminGroups(userId) {
        try {
            // Step 1: Get memberships where user is admin/owner
            const { data: memberships, error: memberError } = await supabaseFetch.query('community_members', {
                select: 'community_id,role',
                eq: { user_id: userId }
            });

            if (memberError) {
                console.error('Error loading admin memberships:', memberError);
                return [];
            }

            // Filter to admin/owner roles
            const adminMemberships = (memberships || []).filter(m =>
                m.role === 'owner' || m.role === 'admin'
            );

            if (adminMemberships.length === 0) {
                return [];
            }

            // Step 2: Fetch community details for these groups
            const communityIds = adminMemberships.map(m => m.community_id);
            const { data: communities, error: commError } = await supabaseFetch.query('communities', {
                select: 'id,name,description,logo_url,hierarchy_level',
                inFilter: { id: communityIds }
            });

            if (commError) {
                console.error('Error loading admin groups:', commError);
                return [];
            }

            return communities || [];
        } catch (error) {
            console.error('Exception in getAdminGroups:', error);
            return [];
        }
    },

    // Pending federation applications to federationGroupId, for the group form's un-designation
    // check (federation-status-guard addendum 1). A convenience count under the caller's own read
    // rights (owners/admins see their group's requests); the database guard
    // trigger_guard_federation_status_change is the authority. Fails open: 0 on error.
    async countPendingFederationApplications(federationGroupId) {
        try {
            const { data, error } = await supabaseFetch.query('group_join_requests', {
                select: 'id',
                eq: { community_id: federationGroupId, request_kind: 'federation', status: 'pending' }
            });
            if (error) {
                console.error('Error counting pending federation applications:', error);
                return 0;
            }
            return (data || []).length;
        } catch (error) {
            console.error('Exception in countPendingFederationApplications:', error);
            return 0;
        }
    },

    // Groups this user could apply to federate with federationGroupId — Federation 4B §1.
    // Two-step query (community_members -> communities), no nested joins, mirrors getAdminGroups.
    // Structural eligibility: admin/owner of the group, the group is top-level
    // (parent_group_id IS NULL), the group is not already in a federation (federation_id IS
    // NULL — "one federation at a time" is an app-layer rule, the live RLS INSERT policy does
    // not check the applicant's own federation_id), and it isn't the target itself. Each
    // returned group also carries hasPendingRequest: true if a pending federation request
    // already exists for this exact (federationGroupId, group) pair — the caller decides
    // whether that excludes it from "apply" or shows a pending state instead.
    async getFederationEligibleApplicantGroups(userId, federationGroupId) {
        try {
            // Two ownership signals name who can act for a group in this codebase —
            // communities.owner_id and a community_members role='owner'/'admin' row — and they
            // can name different users on the same group (probe, 12 Sep 2026: Rede Bio's five
            // member farms all have owner_id = one user, but their community_members owner row
            // names a different one). Michael's ruling: eligibility here takes the union of
            // both signals. Structural filters (top-level, not already federated, not the
            // target itself) are unchanged.
            const { data: memberships, error: memberError } = await supabaseFetch.query('community_members', {
                select: 'community_id,role',
                eq: { user_id: userId }
            });

            if (memberError) {
                console.error('Error loading admin memberships:', memberError);
                return [];
            }

            const { data: ownedCommunities, error: ownedError } = await supabaseFetch.query('communities', {
                select: 'id',
                eq: { owner_id: userId }
            });

            if (ownedError) {
                console.error('Error loading owned communities:', ownedError);
                return [];
            }

            const memberAdminIds = (memberships || [])
                .filter(m => m.role === 'owner' || m.role === 'admin')
                .map(m => m.community_id);

            const ownedIds = (ownedCommunities || []).map(c => c.id);

            const adminIds = [...new Set([...memberAdminIds, ...ownedIds])];

            if (adminIds.length === 0) return [];

            const { data: communities, error: commError } = await supabaseFetch.query('communities', {
                select: 'id,name,logo_url,parent_group_id,federation_id,community_type',
                inFilter: { id: adminIds }
            });

            if (commError) {
                console.error('Error loading admin group details:', commError);
                return [];
            }

            // Authority-type groups (councils, parishes, public bodies) are excluded from the
            // picker — a filter on what's offered, not a database constraint (UI polish brief,
            // 13 Sep: same shape as the person-join ruling; federating is a voluntary
            // association between peer groups, which an authority is not a candidate for).
            const eligible = (communities || []).filter(c =>
                !c.parent_group_id && !c.federation_id && c.id !== federationGroupId
                && c.community_type !== 'authority'
            );

            if (eligible.length === 0) return eligible;

            const { data: pendingRequests, error: pendingError } = await supabaseFetch.query('group_join_requests', {
                select: 'applicant_group_id',
                eq: { community_id: federationGroupId, request_kind: 'federation', status: 'pending' },
                inFilter: { applicant_group_id: eligible.map(g => g.id) }
            });

            if (pendingError) {
                console.error('Error checking pending federation requests:', pendingError);
                return eligible.map(g => ({ ...g, hasPendingRequest: false }));
            }

            const pendingIds = new Set((pendingRequests || []).map(r => r.applicant_group_id));
            return eligible.map(g => ({ ...g, hasPendingRequest: pendingIds.has(g.id) }));
        } catch (error) {
            console.error('Exception in getFederationEligibleApplicantGroups:', error);
            return [];
        }
    },

    // Federation members WITH their approved application answers, for the Group Admin page's
    // owner/admin-only contact-details section (4C-follow-on, 13 Sep). Deliberately NOT part of
    // groupHelpers.getFederationMembers — that function also backs the PUBLIC-facing
    // GroupHomePanel "Federation Members" section, and Michael's ruling is contact details are
    // visible only here, never on any public-facing surface.
    //
    // Returns { isFederation, members }, never a bare array — the caller (AdminGroup.jsx) must
    // show nothing at all when isFederation is false, distinct from isFederation true with zero
    // members. is_federation is read fresh here rather than trusted from a passed-in group prop
    // (AdminGroup.jsx's `group` arrives narrow from several dispatch sites — the same
    // narrow-projection risk this codebase has hit repeatedly on this exact field).
    async getFederationMembersWithApplications(federationGroupId) {
        try {
            const { data: fedGroup, error: fedError } = await supabaseFetch.query('communities', {
                select: 'is_federation,federation_application_schema',
                eq: { id: federationGroupId },
                single: true
            });

            if (fedError) {
                console.error('Error loading federation group row:', fedError);
                return { isFederation: false, members: [] };
            }
            if (!fedGroup?.is_federation) {
                return { isFederation: false, members: [] };
            }

            // Filtered (deleted_at IS NULL), matching 4C §6's display rule — the same shared
            // getFederationMembers used by the public section, not re-derived here.
            const members = await getFederationMembers(federationGroupId);
            if (!members || members.length === 0) {
                return { isFederation: true, members: [] };
            }

            const memberIds = members.map(m => m.id);
            const { data: requests, error: reqError } = await supabaseFetch.query('group_join_requests', {
                select: 'applicant_group_id,request_data,created_at',
                eq: { community_id: federationGroupId, request_kind: 'federation', status: 'approved' },
                inFilter: { applicant_group_id: memberIds },
                order: 'created_at.desc'
            });

            if (reqError) console.error('Error loading federation member applications:', reqError);

            // A member can in principle carry more than one approved row over time (apply →
            // leave → reapply — no leave path exists yet, but the schema doesn't forbid it and
            // this must not guess wrong the day one does). Ordered created_at.desc above, so
            // the first row seen per applicant_group_id is the most recent.
            const requestDataByMember = {};
            (requests || []).forEach(r => {
                if (!(r.applicant_group_id in requestDataByMember)) {
                    requestDataByMember[r.applicant_group_id] = r.request_data;
                }
            });

            const schema = fedGroup?.federation_application_schema || null;

            // A member with no approved application row (4E's group_links migration will
            // produce exactly this) renders as a member with no details, not an error.
            return {
                isFederation: true,
                members: members.map(m => ({
                    ...m,
                    request_data: requestDataByMember[m.id] || null,
                    federation_application_schema: schema
                }))
            };
        } catch (error) {
            console.error('Exception in getFederationMembersWithApplications:', error);
            return { isFederation: false, members: [] };
        }
    },

    // The federation's application-requirement fields (4A's output) — read fresh by id rather
    // than trusting a passed-in group prop, whose shape varies by entry path.
    async getFederationApplicationSchema(federationGroupId) {
        try {
            const { data, error } = await supabaseFetch.query('communities', {
                select: 'federation_application_schema',
                eq: { id: federationGroupId },
                single: true
            });

            if (error) {
                console.error('Error loading federation application schema:', error);
                return [];
            }

            return Array.isArray(data?.federation_application_schema) ? data.federation_application_schema : [];
        } catch (error) {
            console.error('Exception in getFederationApplicationSchema:', error);
            return [];
        }
    },

    // File one federation application (Federation 4B §3) — one row into group_join_requests.
    // user_id is always the acting human (ratified §A) — never the applicant group, never null.
    // request_data is keyed by the schema entry's `key` (4A's stable-key contract), never by
    // label or position. request_message is left unset: that field stays membership-only.
    async applyToFederation(userId, applicantGroupId, federationGroupId, requestData) {
        try {
            // Defensive re-check — the "one federation at a time" rule is app-layer only (the
            // live RLS INSERT policy checks the applicant is top-level, never the applicant's
            // own federation_id), and the eligibility list the caller acted on may be stale.
            const { data: applicant, error: applicantError } = await supabaseFetch.query('communities', {
                select: 'parent_group_id,federation_id',
                eq: { id: applicantGroupId },
                single: true
            });

            if (applicantError || !applicant) {
                return { success: false, status: 'error', message: 'Applicant group not found' };
            }
            if (applicant.parent_group_id) {
                return { success: false, status: 'error', message: 'Only a top-level group can apply to a federation' };
            }
            if (applicant.federation_id) {
                return { success: false, status: 'error', message: 'This group already belongs to a federation' };
            }

            const { data: pending } = await supabaseFetch.query('group_join_requests', {
                select: 'id',
                eq: {
                    community_id: federationGroupId,
                    applicant_group_id: applicantGroupId,
                    request_kind: 'federation',
                    status: 'pending'
                },
                single: true
            });

            if (pending) {
                return { success: false, status: 'pending', message: 'A request is already pending for this group' };
            }

            const { error } = await supabaseFetch.insert('group_join_requests', {
                community_id: federationGroupId,
                applicant_group_id: applicantGroupId,
                user_id: userId,
                request_kind: 'federation',
                request_data: requestData || null,
                status: 'pending'
            });

            if (error) {
                if (isBlockedByRls(error)) {
                    return { success: false, status: 'blocked', message: 'You are not able to apply on behalf of this group' };
                }
                console.error('Error filing federation application:', error);
                return { success: false, status: 'error', message: 'Failed to submit application' };
            }

            return { success: true, status: 'pending' };
        } catch (error) {
            console.error('Exception in applyToFederation:', error);
            return { success: false, status: 'error', message: 'An error occurred' };
        }
    },

    // Get group with full hierarchy info
    async getGroupWithHierarchy(groupId) {
        try {
            // Step 1: Get the group
            const { data: group, error } = await supabaseFetch.query('communities', {
                select: '*',
                eq: { id: groupId },
                single: true
            });

            if (error) {
                console.error('Error loading group with hierarchy:', error);
                return null;
            }

            if (!group) {
                return null;
            }

            // Step 2: If it has a parent, fetch parent details
            if (group.parent_group_id) {
                const { data: parent } = await supabaseFetch.query('communities', {
                    select: 'id,name,hierarchy_level,logo_url',
                    eq: { id: group.parent_group_id },
                    single: true
                });
                group.parent = parent || null;
            } else {
                group.parent = null;
            }

            return group;
        } catch (error) {
            console.error('Exception in getGroupWithHierarchy:', error);
            return null;
        }
    },

    // ========================================
    // INVITATION FUNCTIONS
    // ========================================
    // group_invitations stores a hash of the invited address, never the address
    // itself (Architect ruling R1, 4 Aug 2026). The address travels only in the
    // capability link's signed token, decoded client-side below — see
    // decodeInvitationToken. invite_group_members() is the sole writer; there is
    // no client-side insert into group_invitations any more.

    // Decode the {community_id, email, expiry, nonce} payload carried in a
    // capability token, for display only (e.g. "you were invited as..."). This
    // does not verify the HMAC signature — the row lookup in
    // getInvitationByToken/acceptInvitation is what actually authorises anything;
    // this is purely for showing the user what the link claims before they act.
    decodeInvitationToken(token) {
        try {
            const [payloadHex] = (token || '').split('.');
            if (!payloadHex) return null;
            const bytes = payloadHex.match(/.{1,2}/g)?.map(b => parseInt(b, 16)) || [];
            const json = new TextDecoder().decode(new Uint8Array(bytes));
            const payload = JSON.parse(json);
            return {
                communityId: payload.cid,
                email: payload.email,
                expiresAt: typeof payload.exp === 'number' ? new Date(payload.exp * 1000).toISOString() : null,
                nonce: payload.n
            };
        } catch (error) {
            console.error('Exception decoding invitation token:', error);
            return null;
        }
    },

    // Invite members by email. Returns per-address outcome (already_member /
    // invited / already_invited / rate_limited) and, for newly-created
    // invitations, the full capability link ready to copy. No address is ever
    // enrolled directly — see the comment on invite_group_members() itself.
    async inviteMembers(groupId, emails) {
        try {
            const { data, error } = await supabaseFetch.rpc('invite_group_members', {
                p_community_id: groupId,
                p_emails: emails
            });

            if (error) {
                console.error('Error inviting members:', error);
                return { success: false, error: error.message };
            }

            const results = (data || []).map(row => ({
                email: row.email,
                outcome: row.outcome,
                token: row.invite_token || null,
                link: row.invite_token
                    ? `${window.location.origin}/#/invite/${row.invite_token}`
                    : null
            }));

            return { success: true, results };
        } catch (error) {
            console.error('Exception in inviteMembers:', error);
            return { success: false, error: 'Failed to invite members' };
        }
    },

    // Always-available counts (sent/pending/accepted/expired/revoked) — R4.
    async getInvitationCounts(groupId) {
        try {
            const { data, error } = await supabaseFetch.rpc('get_invitation_counts', {
                p_community_id: groupId
            });

            if (error) {
                console.error('Error getting invitation counts:', error);
                return {};
            }

            const counts = {};
            (data || []).forEach(row => { counts[row.status] = Number(row.count); });
            // R4 asks for "sent" alongside accepted/expired/revoked — the total
            // ever created for this group, computed client-side rather than as
            // a fifth RPC bucket.
            counts.sent = Object.values(counts).reduce((sum, n) => sum + n, 0);
            return counts;
        } catch (error) {
            console.error('Exception in getInvitationCounts:', error);
            return {};
        }
    },

    // Status-by-re-paste — R4. The organiser already holds the address list;
    // this hashes each address and matches against the rows without OurNet ever
    // storing the list itself.
    async checkInvitationStatus(groupId, emails) {
        try {
            const { data, error } = await supabaseFetch.rpc('check_invitation_status', {
                p_community_id: groupId,
                p_emails: emails
            });

            if (error) {
                console.error('Error checking invitation status:', error);
                return { success: false, error: error.message };
            }

            return { success: true, results: data || [] };
        } catch (error) {
            console.error('Exception in checkInvitationStatus:', error);
            return { success: false, error: 'Failed to check invitation status' };
        }
    },

    // Revoke a pending invitation — R5. Keyed by token: R4 keeps no browsable
    // list, so the only thing the owner ever holds is the link itself.
    async revokeInvitation(inviteToken) {
        try {
            const { data, error } = await supabaseFetch.rpc('revoke_invitation', {
                p_invite_token: inviteToken
            });

            if (error) {
                console.error('Error revoking invitation:', error);
                return false;
            }

            return data === true;
        } catch (error) {
            console.error('Exception in revokeInvitation:', error);
            return false;
        }
    },

    // Get invitation by token (via SECURITY DEFINER RPC — safe for anon/guest callers).
    // null = no such invitation; { error: true } = it could not be read (so the page can say "try
    // again" rather than "this link isn't valid").
    async getInvitationByToken(token) {
        try {
            // Step 1: Get the invitation via RPC (does not expose group_invitations table)
            const { data: rpcResult, error } = await supabaseFetch.rpc('get_invitation_by_token', {
                p_token: token
            });
            const invitation = Array.isArray(rpcResult) ? rpcResult[0] || null : rpcResult;

            if (error && error.status !== 404) {
                console.error('Error getting invitation:', error.code || error.status);
                return { error: true };
            }
            if (!invitation) {
                return null;
            }

            // Step 2: Fetch community details
            let community = null;
            if (invitation.community_id) {
                const { data: comm } = await supabaseFetch.query('communities', {
                    select: 'id,name,description,logo_url,cover_image_url,boundary_ids',
                    eq: { id: invitation.community_id },
                    single: true
                });
                community = comm;
            }

            // Step 2b: the group's place (Architect, 4 Oct 2026, Phase 3 Q2) — '' when none is found.
            let place = community ? await this.getGroupPlace(community.boundary_ids) : '';

            // Step 2c: what the invitation itself lets its holder see (addendum 4 item 3): for a
            // private group the row above is for members only, so its details come from here.
            const introduction = invitation.community_id
                ? await this.getGroupIntroduction(invitation.community_id, token)
                : { found: false };
            if (!community && introduction.found) {
                community = {
                    id: introduction.id,
                    name: introduction.name,
                    description: introduction.description,
                    logo_url: introduction.logoUrl
                };
                place = introduction.place;
            }

            // Step 3: Fetch inviter details
            let inviter = null;
            if (invitation.invited_by) {
                const { data: profiles } = await supabaseFetch.rpc('get_public_profiles', {
                    p_user_ids: [invitation.invited_by]
                });
                inviter = profiles?.[0] || null;
            }

            // Step 4: Decode the address from the token itself — the row no
            // longer holds it (R1). Display only; not an authorisation check.
            const decoded = this.decodeInvitationToken(token);

            return {
                ...invitation,
                community,
                place,
                introduction,
                inviter,
                invitedEmail: decoded?.email || null
            };
        } catch (error) {
            console.error('Exception in getInvitationByToken:', error?.name);
            return { error: true };
        }
    },

    // Accept an invitation
    async acceptInvitation(token, userId) {
        try {
            const { data, error } = await supabaseFetch.rpc('accept_invitation', {
                token: token,
                user_uuid: userId
            });

            if (error) {
                console.error('Error accepting invitation:', error);
                return { success: false, error: error.message };
            }

            // RPC returns a table, get first row. Its English message becomes a code the page
            // translates (acceptInvitationCode; addendum 1 item 5).
            const result = Array.isArray(data) ? data[0] : data;
            const code = acceptInvitationCode(result);
            return {
                success: code === 'joined' || code === 'already_member',
                code,
                groupId: result?.group_id || null,
                groupName: result?.group_name || null
            };
        } catch (error) {
            console.error('Exception in acceptInvitation:', error);
            return { success: false, code: 'failed' };
        }
    },

    // A group's place, by the database's _group_place rule (pickGroupPlace; the rule is private in
    // the database, so it is read here from admin_boundaries). '' when none is found or on error.
    async getGroupPlace(boundaryIds) {
        const ids = (boundaryIds || []).slice(0, GROUP_PLACE_MAX_AREAS).filter(v => v != null).map(Number);
        if (ids.length === 0) return '';
        try {
            const { data: rows, error } = await supabaseFetch.query('admin_boundaries', {
                select: 'id,name,parent_id',
                inFilter: { id: [...new Set(ids)] }
            });
            if (error || !rows) return '';
            const parentId = sharedParentId(rows);
            const parentNames = parentId != null ? await getBoundaryNames([parentId]) : {};
            return pickGroupPlace(ids, rows, id => parentNames[id] || null);
        } catch (error) {
            console.error('Exception in getGroupPlace:', error);
            return '';
        }
    },

    // ---- Shared join link (Build 2 §5). One live link per group; group admins only, except the
    // preview (anyone holding the link) and the join (signed in). Codes, never English.

    joinLinkUrl(token) {
        return `${window.location.origin}/#/join/${token}`;
    },

    // What a group's Introduction page may show, by get_group_introduction (20261004_group_types):
    // a public or findable group to anyone; an invitation-only group to its members and to the holder
    // of a valid invitation or live join link for it (token). { found: false } when the group can't
    // be shown (missing, being deleted, or hidden from this reader — deliberately one answer);
    // { error: true } when it could not be read.
    async getGroupIntroduction(groupId, token = null) {
        if (!groupId) return { found: false };
        try {
            const { data, error } = await supabaseFetch.rpc('get_group_introduction', {
                p_community_id: groupId,
                p_token: token || null
            });
            if (error) return { error: true };
            const row = Array.isArray(data) ? data[0] : data;
            if (!row) return { found: false };
            return {
                found: true,
                id: row.group_id,
                name: row.group_name || '',
                place: row.group_place || '',
                logoUrl: row.group_logo_url || null,
                heroUrl: row.group_hero_image_url || row.group_cover_image_url || row.group_logo_url || null,
                heroThumbnailUrl: row.group_hero_thumbnail_url || null,
                description: row.group_description || '',
                accessType: row.access_type,
                isMember: row.is_member === true,
                viaToken: row.via_token === true,
                introLeft: row.intro_left_content || '',
                introRight: row.intro_right_content || '',
                introLeftImages: row.intro_left_images || [],
                introRightImages: row.intro_right_images || [],
                hasIntroduction: !!(row.intro_left_content || row.intro_right_content)
            };
        } catch (error) {
            console.error('Exception in getGroupIntroduction:', error?.name);
            return { error: true };
        }
    },

    // { groupId, groupName, place, state: valid | expired | revoked | group_unavailable | not_found }
    async getJoinLinkPreview(token) {
        try {
            const { data, error } = await supabaseFetch.rpc('get_join_link_preview', { p_token: token });
            const row = Array.isArray(data) ? data[0] : data;
            if (error || !row) return { state: 'error' };
            return {
                groupId: row.group_id || null,
                groupName: row.group_name || null,
                place: row.group_place || '',
                state: row.state
            };
        } catch (error) {
            console.error('Exception in getJoinLinkPreview:', error);
            return { state: 'error' };
        }
    },

    // { success, outcome: joined | already_member | blocked | rate_limited | expired | revoked |
    //   group_unavailable | not_found | not_authenticated | error, groupId, groupName }
    async acceptGroupJoinLink(token) {
        try {
            const { data, error } = await supabaseFetch.rpc('accept_group_join_link', { p_token: token });
            const row = Array.isArray(data) ? data[0] : data;
            if (error || !row) return { success: false, outcome: 'error' };
            return {
                success: row.success === true,
                outcome: row.outcome,
                groupId: row.group_id || null,
                groupName: row.group_name || null
            };
        } catch (error) {
            console.error('Exception in acceptGroupJoinLink:', error);
            return { success: false, outcome: 'error' };
        }
    },

    // The group's live link, or null: { token, link, createdAt, expiresAt, joins, isExpired }.
    // A switched-off link is not returned. { error: true } when it could not be read.
    async getGroupJoinLink(groupId) {
        try {
            const { data, error } = await supabaseFetch.rpc('get_group_join_link', { p_community_id: groupId });
            if (error) return { error: true };
            const row = Array.isArray(data) ? data[0] : data;
            if (!row?.link_token) return null;
            return {
                token: row.link_token,
                link: this.joinLinkUrl(row.link_token),
                createdAt: row.link_created_at,
                expiresAt: row.link_expires_at,
                joins: Number(row.link_joins) || 0,
                isExpired: row.link_is_expired === true
            };
        } catch (error) {
            console.error('Exception in getGroupJoinLink:', error);
            return { error: true };
        }
    },

    // Creates the group's link, replacing (switching off) any live one. Expiry 1–90 days.
    async createGroupJoinLink(groupId, expiresInDays) {
        try {
            const { data, error } = await supabaseFetch.rpc('create_group_join_link', {
                p_community_id: groupId,
                p_expires_in_days: expiresInDays
            });
            const row = Array.isArray(data) ? data[0] : data;
            if (error || !row?.link_token) {
                if (error) console.error('Error creating join link:', error.code || error.status);
                return { success: false };
            }
            return { success: true };
        } catch (error) {
            console.error('Exception in createGroupJoinLink:', error);
            return { success: false };
        }
    },

    async revokeGroupJoinLink(groupId) {
        try {
            const { data, error } = await supabaseFetch.rpc('revoke_group_join_link', { p_community_id: groupId });
            if (error) return false;
            return data === true;
        } catch (error) {
            console.error('Exception in revokeGroupJoinLink:', error);
            return false;
        }
    },

    // ---- Invitation email (Build 2 §3–§4). Emails existing personal invitations through the
    // send-invitation Edge Function, on the admin's own token (never a service key). At most 20 per
    // call (the function's limit), so longer lists go in turns. Returns { [token]: outcome };
    // a token whose turn never reached the function is 'not_attempted'. Never logs a token or an
    // address.
    async sendInvitationEmails(tokens, locale) {
        const outcomes = {};
        const list = [...new Set((tokens || []).filter(Boolean))];
        list.forEach(token => { outcomes[token] = 'not_attempted'; });
        if (list.length === 0) return outcomes;
        let authToken = null;
        try {
            authToken = await supabaseFetch.getAuthToken();
        } catch (error) {
            console.error('sendInvitationEmails: no session', error?.name);
        }
        if (!authToken) return outcomes;
        const BATCH = 20;
        for (let i = 0; i < list.length; i += BATCH) {
            try {
                const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-invitation`, {
                    method: 'POST',
                    headers: {
                        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ invite_tokens: list.slice(i, i + BATCH), locale })
                });
                if (!response.ok) {
                    console.error('send-invitation failed:', response.status);
                    continue;
                }
                const body = await response.json().catch(() => ({}));
                (body.results || []).forEach(r => {
                    if (r && r.invite_token in outcomes) outcomes[r.invite_token] = String(r.outcome);
                });
            } catch (error) {
                console.error('Exception in sendInvitationEmails:', error?.name);
            }
        }
        return outcomes;
    },

    // One click from the invitation email, no sign-in. Call only on a button press (mail scanners
    // open every link in a message). true = done; false = the link is not a valid invitation link.
    async unsubscribeInvitationEmail(token) {
        try {
            const { data, error } = await supabaseFetch.rpc('unsubscribe_invitation_email', { p_invite_token: token });
            if (error) return { ok: false, error: true };
            return { ok: data === true };
        } catch (error) {
            console.error('Exception in unsubscribeInvitationEmail:', error?.name);
            return { ok: false, error: true };
        }
    },

    async reportInvitation(token) {
        try {
            const { data, error } = await supabaseFetch.rpc('report_invitation', { p_invite_token: token });
            if (error) return { ok: false, error: true };
            return { ok: data === true };
        } catch (error) {
            console.error('Exception in reportInvitation:', error?.name);
            return { ok: false, error: true };
        }
    },

    // Platform admins: reports per group (Build 2 §4 — stored only, a count in the Admin Panel).
    async getInvitationReportCounts() {
        try {
            const { data, error } = await supabaseFetch.rpc('get_invitation_report_counts', {});
            if (error) return { error: true, rows: [] };
            return { rows: data || [] };
        } catch (error) {
            console.error('Exception in getInvitationReportCounts:', error);
            return { error: true, rows: [] };
        }
    },

    // Soft-delete a group (sets deleted_at, cascades to sub-groups)
    async softDeleteGroup(groupId, userId) {
        try {
            console.log('Soft-deleting group:', groupId, 'by user:', userId);
            const { data, error } = await supabaseFetch.rpc('soft_delete_group', {
                group_uuid: groupId,
                user_uuid: userId
            });

            if (error) {
                console.error('Error soft-deleting group:', error);
                return { success: false, error: error.message };
            }

            return data;
        } catch (error) {
            console.error('Exception in softDeleteGroup:', error);
            return { success: false, error: error.message };
        }
    },

    // Undo soft-delete (restore group and its sub-groups)
    async undoSoftDelete(groupId, userId) {
        try {
            console.log('Restoring soft-deleted group:', groupId, 'by user:', userId);
            const { data, error } = await supabaseFetch.rpc('undo_soft_delete_group', {
                group_uuid: groupId,
                user_uuid: userId
            });

            if (error) {
                console.error('Error restoring group:', error);
                return { success: false, error: error.message };
            }

            return data;
        } catch (error) {
            console.error('Exception in undoSoftDelete:', error);
            return { success: false, error: error.message };
        }
    },

    // Is this group closing (Roadmap #103)? A group is closing while deleted_at is set — the one
    // predicate the database uses too (public.group_is_closing). RLS decides who may read the row:
    // members and the owner of a closing group can; anyone else gets null, as for any hidden group.
    // Returns { closing, closesOn, ownerId } or null when the row cannot be read.
    async getGroupClosingState(groupId) {
        try {
            if (!groupId) return null;
            const { data, error } = await supabaseFetch.query('communities', {
                select: 'id, owner_id, deleted_at, deletion_scheduled_at',
                eq: { id: groupId },
                single: true
            });
            if (error || !data) return null;
            return {
                closing: !!data.deleted_at,
                closesOn: data.deletion_scheduled_at || null,
                ownerId: data.owner_id || null
            };
        } catch (error) {
            console.error('Exception in getGroupClosingState:', error);
            return null;
        }
    },

    // Group privacy lock (docs/briefs/2026-09-27-group-privacy-lock.md): true when the group's
    // privacy can no longer change because people other than its owner have posted in it. The
    // RPC answers only the owner, community admins and platform admins (NULL otherwise). Any
    // failure returns false — the form fails open and the database guard stays authoritative.
    async getGroupPrivacyLocked(communityId) {
        try {
            const { data, error } = await supabaseFetch.rpc('group_privacy_locked', {
                p_community_id: communityId
            });
            if (error) {
                console.warn('Could not check group privacy lock for', communityId, error);
                return false;
            }
            return data === true;
        } catch (error) {
            console.warn('Exception in getGroupPrivacyLocked:', error);
            return false;
        }
    }
};

// ============================================
// USER ACTIONS SERVICE - For likes, ratings, etc.
// ============================================

// Session-level dedup for message engagement tracking — avoids redundant
// network requests when the same message is re-rendered (area change, thread reload).
const _trackedEngagements = new Set();

export const userActionService = {
    // Set or update a user rating for an article
    async setArticleRating(userId, articleId, rating) {
        try {
            if (!userId || !articleId || !rating) {
                console.error('Missing required parameters for setArticleRating');
                return false;
            }

            // rating should be 1-5, which maps to rate_1 through rate_5
            const actionType = `rate_${rating}`;

            // Check if user already has a rating for this article
            const { data: existing, error: fetchError } = await supabaseFetch.query('user_actions', {
                select: 'id,action_type',
                eq: { user_id: userId, target_id: articleId, target_type: 'article' },
                like: { action_type: 'rate_*' },
                single: true
            });

            if (fetchError && fetchError.code !== 'PGRST116') {
                console.error('Error checking existing rating:', fetchError);
                return false;
            }

            if (existing) {
                // Update existing rating
                console.log('Updating existing rating:', existing.id, 'to', actionType);
                const { data: updateData, error: updateError } = await supabaseFetch.update('user_actions', {
                    action_type: actionType,
                    updated_at: new Date().toISOString()
                }, { eq: { id: existing.id } });

                console.log('Update result - data:', updateData);
                console.log('Update result - error:', updateError);

                if (updateError) {
                    console.error('Error updating rating:', updateError);
                    console.error('Full error details:', JSON.stringify(updateError, null, 2));
                    return false;
                }
            } else {
                // Insert new rating
                const { error: insertError } = await supabaseFetch.insert('user_actions', {
                    user_id: userId,
                    target_id: articleId,
                    target_type: 'article',
                    action_type: actionType,
                    created_at: new Date().toISOString()
                }, { returnMinimal: true });

                if (insertError) {
                    console.error('Error inserting rating:', insertError);
                    return false;
                }
            }

            return true;
        } catch (error) {
            console.error('Exception in setArticleRating:', error);
            return false;
        }
    },

    // Get user's rating for an article
    async getArticleRating(userId, articleId) {
        try {
            if (!userId || !articleId) {
                return null;
            }

            const { data, error } = await supabaseFetch.query('user_actions', {
                select: 'action_type',
                eq: { user_id: userId, target_id: articleId, target_type: 'article' },
                like: { action_type: 'rate_*' },
                single: true
            });

            if (error && error.code !== 'PGRST116') {
                console.error('Error fetching article rating:', error);
                return null;
            }

            if (!data) {
                return null;
            }

            // Extract rating number from action_type (e.g., 'rate_3' -> 3)
            const ratingMatch = data.action_type.match(/rate_(\d+)/);
            return ratingMatch ? parseInt(ratingMatch[1]) : null;
        } catch (error) {
            console.error('Exception in getArticleRating:', error);
            return null;
        }
    },

    // Set user's rating for an event (1-5 stars)
    async setEventRating(userId, eventId, rating) {
        try {
            if (!userId || !eventId || !rating) {
                console.error('Missing required parameters for setEventRating');
                return false;
            }

            // rating should be 1-5, which maps to rate_1 through rate_5
            const actionType = `rate_${rating}`;

            // Check if user already has a rating for this event
            const { data: existing, error: fetchError } = await supabaseFetch.query('user_actions', {
                select: 'id,action_type',
                eq: { user_id: userId, target_id: eventId, target_type: 'event' },
                like: { action_type: 'rate_*' },
                single: true
            });

            if (fetchError && fetchError.code !== 'PGRST116') {
                console.error('Error checking existing event rating:', fetchError);
                return false;
            }

            if (existing) {
                // Update existing rating
                console.log('Updating existing event rating:', existing.id, 'to', actionType);
                const { data: updateData, error: updateError } = await supabaseFetch.update('user_actions', {
                    action_type: actionType,
                    updated_at: new Date().toISOString()
                }, { eq: { id: existing.id } });

                if (updateError) {
                    console.error('Error updating event rating:', updateError);
                    return false;
                }

                console.log('Event rating updated successfully:', updateData);
                return true;
            } else {
                // Insert new rating
                console.log('Inserting new event rating:', actionType);
                const { data: insertData, error: insertError } = await supabaseFetch.insert('user_actions', {
                    user_id: userId,
                    target_id: eventId,
                    target_type: 'event',
                    action_type: actionType,
                    created_at: new Date().toISOString()
                });

                if (insertError) {
                    console.error('Error inserting event rating:', insertError);
                    return false;
                }

                console.log('Event rating inserted successfully:', insertData);
                return true;
            }
        } catch (error) {
            console.error('Exception in setEventRating:', error);
            return false;
        }
    },

    // Get user's rating for an event
    async getEventRating(userId, eventId) {
        try {
            if (!userId || !eventId) {
                return null;
            }

            const { data, error } = await supabaseFetch.query('user_actions', {
                select: 'action_type',
                eq: { user_id: userId, target_id: eventId, target_type: 'event' },
                like: { action_type: 'rate_*' },
                single: true
            });

            if (error && error.code !== 'PGRST116') {
                console.error('Error fetching event rating:', error);
                return null;
            }

            if (!data) {
                return null;
            }

            // Extract rating number from action_type (e.g., 'rate_3' -> 3)
            const ratingMatch = data.action_type.match(/rate_(\d+)/);
            return ratingMatch ? parseInt(ratingMatch[1]) : null;
        } catch (error) {
            console.error('Exception in getEventRating:', error);
            return null;
        }
    },

    // Set user's like/dislike for a message (chat message)
    // Returns { success: boolean, newStatus: 'like' | 'dislike' | null }
    async setMessageLikeDislike(userId, messageId, actionType) {
        try {
            if (!userId || !messageId || !actionType) {
                console.error('Missing required parameters for setMessageLikeDislike');
                return { success: false, newStatus: null };
            }

            // actionType should be 'like' or 'dislike'
            if (actionType !== 'like' && actionType !== 'dislike') {
                console.error('Invalid action type for setMessageLikeDislike');
                return { success: false, newStatus: null };
            }

            // Check BOTH like and dislike records to handle all cases
            // Note: target_type is 'chat' not 'message' after Phase 1 migration
            const { data: allRecords, error: fetchError } = await supabaseFetch.query('user_actions', {
                select: 'id, action_type',
                eq: {
                    user_id: userId,
                    target_id: messageId,
                    target_type: 'chat'
                },
                in: { action_type: ['like', 'dislike'] }
            });

            if (fetchError) {
                console.error('Error checking existing message like/dislike:', fetchError);
                return { success: false, newStatus: null };
            }

            // Find records by type
            const likeRecord = allRecords?.find(r => r.action_type === 'like');
            const dislikeRecord = allRecords?.find(r => r.action_type === 'dislike');
            const sameActionRecord = actionType === 'like' ? likeRecord : dislikeRecord;
            const oppositeActionRecord = actionType === 'like' ? dislikeRecord : likeRecord;

            console.log(`[setMessageLikeDislike] Message ${messageId.substring(0, 8)}, action: ${actionType}`, {
                likeRecord: !!likeRecord,
                dislikeRecord: !!dislikeRecord,
                sameActionRecord: !!sameActionRecord,
                oppositeActionRecord: !!oppositeActionRecord
            });

            if (sameActionRecord) {
                // User clicked the same action again - remove it (toggle off)
                console.log(`[setMessageLikeDislike] Toggling off ${actionType}, deleting ID: ${sameActionRecord.id}`);
                const { error: deleteError } = await supabaseFetch.delete('user_actions', {
                    eq: { id: sameActionRecord.id }
                });

                if (deleteError) {
                    console.error('Error removing message like/dislike:', deleteError);
                    return { success: false, newStatus: null };
                }
                console.log(`[setMessageLikeDislike] Deleted successfully`);
                return { success: true, newStatus: null };
            } else if (oppositeActionRecord) {
                // User is switching from like to dislike or vice versa
                console.log(`[setMessageLikeDislike] Switching from ${oppositeActionRecord.action_type} to ${actionType}, updating ID: ${oppositeActionRecord.id}`);
                const { error: updateError } = await supabaseFetch.update('user_actions',
                    {
                        action_type: actionType,
                        updated_at: new Date().toISOString()
                    },
                    {
                        eq: { id: oppositeActionRecord.id }
                    }
                );

                if (updateError) {
                    console.error('Error updating message like/dislike:', updateError);
                    return { success: false, newStatus: null };
                }
                console.log(`[setMessageLikeDislike] Updated successfully`);
                return { success: true, newStatus: actionType };
            } else {
                // Insert new like/dislike
                console.log(`[setMessageLikeDislike] No existing record, inserting new ${actionType}`);
                const { error: insertError } = await supabaseFetch.insert('user_actions', {
                    user_id: userId,
                    target_id: messageId,
                    target_type: 'chat',
                    action_type: actionType,
                    created_at: new Date().toISOString()
                });

                if (insertError) {
                    console.error('Error inserting message like/dislike:', insertError);
                    return { success: false, newStatus: null };
                }
                console.log(`[setMessageLikeDislike] Inserted successfully`);
                return { success: true, newStatus: actionType };
            }
        } catch (error) {
            console.error('Exception in setMessageLikeDislike:', error);
            return { success: false, newStatus: null };
        }
    },

    // Get user's like/dislike status for a message
    async getMessageLikeDislike(userId, messageId) {
        try {
            if (!userId || !messageId) {
                return null;
            }

            // Note: target_type is 'chat' not 'message' after Phase 1 migration
            const { data, error } = await supabaseFetch.query('user_actions', {
                select: 'action_type',
                eq: {
                    user_id: userId,
                    target_id: messageId,
                    target_type: 'chat'
                },
                in: { action_type: ['like', 'dislike'] },
                limit: 1
            });

            if (error) {
                console.error('Error fetching message like/dislike:', error);
                return null;
            }

            return data?.[0]?.action_type || null;
        } catch (error) {
            console.error('Exception in getMessageLikeDislike:', error);
            return null;
        }
    },

    // Get real-time like/dislike counts for a message from user_actions
    async getMessageCounts(messageId) {
        try {
            if (!messageId) {
                return { likeCount: 0, dislikeCount: 0 };
            }

            const { data, error } = await supabaseFetch.query('user_actions', {
                select: 'action_type',
                eq: {
                    target_id: messageId,
                    target_type: 'chat'
                },
                in: { action_type: ['like', 'dislike'] }
            });

            if (error) {
                console.error('Error fetching message counts:', error);
                return { likeCount: 0, dislikeCount: 0 };
            }

            const likeCount = data?.filter(r => r.action_type === 'like').length || 0;
            const dislikeCount = data?.filter(r => r.action_type === 'dislike').length || 0;

            return { likeCount, dislikeCount };
        } catch (error) {
            console.error('Exception in getMessageCounts:', error);
            return { likeCount: 0, dislikeCount: 0 };
        }
    },

    // Mark user as attending an event
    async attendEvent(userId, eventId) {
        try {
            if (!userId || !eventId) {
                console.error('Missing required parameters for attendEvent');
                return false;
            }

            // Check if user is already attending
            const { data: existing, error: fetchError } = await supabaseFetch.query('user_actions', {
                select: 'id',
                eq: { user_id: userId, target_id: eventId, target_type: 'event', action_type: 'attend' },
                single: true
            });

            if (fetchError && fetchError.code !== 'PGRST116') {
                console.error('Error checking attendance:', fetchError);
                return false;
            }

            if (existing) {
                // User is already attending
                return 'already_attending';
            }

            // Insert new attendance record
            const { error: insertError } = await supabaseFetch.insert('user_actions', {
                user_id: userId,
                target_id: eventId,
                target_type: 'event',
                action_type: 'attend',
                created_at: new Date().toISOString()
            }, { returnMinimal: true });

            if (insertError) {
                console.error('Error inserting attendance:', insertError);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Exception in attendEvent:', error);
            return false;
        }
    },

    // Check if user is attending an event
    async isAttendingEvent(userId, eventId) {
        try {
            if (!userId || !eventId) {
                return false;
            }

            const { data, error } = await supabaseFetch.query('user_actions', {
                select: 'id',
                eq: { user_id: userId, target_id: eventId, target_type: 'event', action_type: 'attend' },
                single: true
            });

            if (error && error.code !== 'PGRST116') {
                console.error('Error checking attendance:', error);
                return false;
            }

            return !!data;
        } catch (error) {
            console.error('Exception in isAttendingEvent:', error);
            return false;
        }
    },

    // Remove user's attendance from an event
    async leaveEvent(userId, eventId) {
        try {
            if (!userId || !eventId) {
                console.error('Missing required parameters for leaveEvent');
                return false;
            }

            // Delete the attendance record
            const { error } = await supabaseFetch.delete('user_actions', {
                eq: { user_id: userId, target_id: eventId, target_type: 'event', action_type: 'attend' }
            });

            if (error) {
                console.error('Error removing attendance:', error);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Exception in leaveEvent:', error);
            return false;
        }
    },

    // Track message engagement (view, like, dislike, share) in message_engagement table
    async trackMessageEngagement(userId, messageId, engagementType) {
        try {
            if (!userId || !messageId || !engagementType) {
                console.error('Missing required parameters for trackMessageEngagement');
                return { success: false };
            }

            // Validate engagement type
            if (!['view', 'like', 'dislike', 'share'].includes(engagementType)) {
                console.error('Invalid engagement type for trackMessageEngagement:', engagementType);
                return { success: false };
            }

            // Session-level dedup — skip if already tracked this session
            const dedupKey = `${messageId}:${userId}:${engagementType}`;
            if (_trackedEngagements.has(dedupKey)) {
                return { success: true };
            }

            // Pre-fetch auth token — if session isn't restored yet, skip silently.
            // An unauthenticated insert would hit anon-key fallback and 403 on RLS.
            const authToken = await supabaseFetch.getAuthToken();
            if (!authToken) {
                return { success: false };
            }

            // Insert engagement record.
            // returnMinimal: avoids a read-back SELECT that would hit the
            // SELECT USING(false) policy on message_engagement.
            // NOTE: Do NOT use ignoreDuplicates here. PostgREST's
            // resolution=ignore-duplicates produces ON CONFLICT DO NOTHING, but
            // Postgres can't see the conflicting row through SELECT USING(false),
            // so it attempts a plain INSERT which then 403s on the WITH CHECK.
            // Duplicates are handled app-side: session Set dedup (above) +
            // 23505 error code suppression (below).
            const { error } = await supabaseFetch.insert('message_engagement', {
                message_id: messageId,
                user_id: userId,
                engagement_type: engagementType,
                created_at: new Date().toISOString()
            }, { authToken, returnMinimal: true });

            if (error) {
                // 23505 = unique constraint violation — expected on repeat view
                // (session Set catches most, but races / fresh sessions can still hit this).
                // A demo-write refusal (stale demo content after leaving demo mode) is expected too.
                if (error.code !== '23505' && !error.message?.includes('23505') && !isDemoRefusal(error)) {
                    console.error('Error tracking message engagement:', error);
                }
                return { success: false };
            }

            _trackedEngagements.add(dedupKey);
            return { success: true };
        } catch (error) {
            console.error('Exception in trackMessageEngagement:', error);
            return { success: false };
        }
    },

    // Track article/event engagement (view, like, comment, share) in article_engagement table
    // Inserts a record with ON CONFLICT ignore (UNIQUE constraint prevents duplicates)
    async trackArticleEngagement(articleId, userId, engagementType) {
        if (!articleId || !userId) return { success: false };
        if (!['view', 'like', 'comment', 'share'].includes(engagementType)) {
            console.error('Invalid engagement type for trackArticleEngagement:', engagementType);
            return { success: false };
        }
        try {
            const { error } = await supabaseFetch.insert('article_engagement', {
                article_id: articleId,
                user_id: userId,
                engagement_type: engagementType,
                created_at: new Date().toISOString()
            });

            if (error) {
                // Duplicate entries are expected (UNIQUE constraint), don't log as error; nor a
                // demo-write refusal (stale demo content after leaving demo mode)
                if (error.code !== '23505' && !isDemoRefusal(error)) {
                    console.error('Error tracking article engagement:', error);
                }
                return { success: false };
            }

            return { success: true };
        } catch (error) {
            console.error('Exception in trackArticleEngagement:', error);
            return { success: false };
        }
    },

    // Convenience wrappers matching the old reputationCalculator API
    async trackView(articleId, userId) {
        return this.trackArticleEngagement(articleId, userId, 'view');
    },

    async trackComment(articleId, userId) {
        return this.trackArticleEngagement(articleId, userId, 'comment');
    },

    async trackShare(articleId, userId) {
        return this.trackArticleEngagement(articleId, userId, 'share');
    },

    // Count article_engagement rows of one type for an article (view/comment counters on detail
    // views). Returns a number, or null on error. Was an inline supabase-js count in
    // ArticleDetail.jsx; now via supabaseFetch so it follows the demo read target (Phase B1).
    async getArticleEngagementCount(articleId, engagementType) {
        if (!articleId || !engagementType) return null;
        const { count, error } = await supabaseFetch.query('article_engagement', {
            select: 'id',
            eq: { article_id: articleId, engagement_type: engagementType },
            count: 'exact',
            head: true
        });
        if (error) return null;
        return count ?? null;
    },

    // Submit a content report
    async submitReport(userId, reportData) {
        try {
            if (!userId || !reportData || !reportData.targetType || !reportData.targetId || !reportData.category) {
                console.error('Missing required parameters for submitReport');
                return { success: false };
            }

            // Validate target_type
            if (!['article', 'event', 'chat', 'comment'].includes(reportData.targetType)) {
                console.error('Invalid target_type for submitReport:', reportData.targetType);
                return { success: false };
            }

            // Validate category
            if (!['offensive', 'spam', 'harassment', 'off_topic'].includes(reportData.category)) {
                console.error('Invalid category for submitReport:', reportData.category);
                return { success: false };
            }

            // Insert report.
            // article_id is the legacy FK column — populate it for article reports so
            // the admin query can join on it to retrieve the article title.
            const insertPayload = {
                reporter_id: userId,
                target_type: reportData.targetType,
                target_id: reportData.targetId,
                category: reportData.category,
                details: reportData.details || null,
                status: 'pending',
                created_at: new Date().toISOString()
            };
            if (reportData.targetType === 'article') {
                insertPayload.article_id = reportData.targetId;
            }
            const { error } = await supabaseFetch.insert('content_reports', insertPayload);

            if (error) {
                console.error('Error submitting report:', error);
                return { success: false };
            }

            return { success: true };
        } catch (error) {
            console.error('Exception in submitReport:', error);
            return { success: false };
        }
    }
};

// Smart Links Service - Phase 1
export const smartLinkService = {
    // Generate an 8-character short code (alphanumeric only)
    generateShortCode() {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let code = '';
        for (let i = 0; i < 8; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return code;
    },

    // Generate a unique short code (collision-checked)
    async generateUniqueShortCode() {
        let code, exists;
        let attempts = 0;
        const maxAttempts = 10; // Safety limit to prevent infinite loops

        do {
            code = this.generateShortCode();

            // Check if code already exists
            const { data } = await supabaseFetch.query('smart_links', {
                select: 'id',
                eq: { short_code: code }
            });

            exists = data && data.length > 0;
            attempts++;

            if (attempts >= maxAttempts && exists) {
                console.error('Failed to generate unique short code after', maxAttempts, 'attempts');
                throw new Error('Failed to generate unique short code');
            }
        } while (exists);

        return code;
    },

    // Get smart link by slug OR short_code (with usability check)
    async getSmartLinkBySlug(slugOrCode) {
        try {
            // First try the RPC function (works for slug)
            const { data: rpcData, error: rpcError } = await supabaseFetch.rpc('get_smart_link_by_slug', {
                p_slug: slugOrCode
            });

            if (!rpcError && rpcData && rpcData.length > 0) {
                return rpcData[0];
            }

            // If RPC fails or returns nothing, try direct query for short_code
            const { data, error } = await supabaseFetch.query('smart_links', {
                select: '*',
                or: `slug.eq.${slugOrCode},short_code.eq.${slugOrCode}`
            });

            if (error) {
                console.error('Error fetching smart link:', error);
                return null;
            }

            if (data && data.length > 0) {
                // Add is_usable check manually since we're not using the RPC
                const link = data[0];
                link.is_usable = link.is_active &&
                                 (!link.expires_at || new Date(link.expires_at) > new Date()) &&
                                 (!link.max_uses || link.use_count < link.max_uses);
                return link;
            }

            return null;
        } catch (error) {
            console.error('Exception in getSmartLinkBySlug:', error);
            return null;
        }
    },

    // Create a new smart link with short_code
    async createSmartLink({ slug, targetType, targetId, linkType = 'general', maxUses = null, expiresAt = null, metadata = {}, label = null }) {
        try {
            // Generate unique short code
            const shortCode = await this.generateUniqueShortCode();

            // For backward compatibility, generate slug if not provided
            if (!slug) {
                slug = shortCode; // Use short code as slug fallback
            }

            const { data, error } = await supabaseFetch.insert('smart_links', {
                slug,
                short_code: shortCode,
                label,
                target_type: targetType,
                target_id: targetId,
                link_type: linkType,
                max_uses: maxUses,
                expires_at: expiresAt,
                metadata,
                created_by: null // Will be set by RLS policy using auth.uid()
            });

            if (error) {
                console.error('Error creating smart link:', error);
                return { success: false, error: error.message };
            }

            return { success: true, data };
        } catch (error) {
            console.error('Exception in createSmartLink:', error);
            return { success: false, error: error.message };
        }
    },

    // Get all smart links created by a user for a specific target
    async getSmartLinksForTarget(targetType, targetId) {
        try {
            const { data, error } = await supabaseFetch.query('smart_links', {
                select: 'id,slug,short_code,label,link_type,max_uses,expires_at,use_count,is_active,created_at',
                eq: { target_type: targetType, target_id: targetId, is_active: true },
                order: 'created_at.desc'
            });

            if (error) {
                console.error('Error fetching smart links for target:', error);
                return [];
            }

            return data || [];
        } catch (error) {
            console.error('Exception in getSmartLinksForTarget:', error);
            return [];
        }
    },

    // Increment use count when a link is used
    async incrementSmartLinkUse(linkId) {
        try {
            const { error } = await supabaseFetch.rpc('increment_smart_link_use', {
                link_id: linkId
            });

            if (error) {
                console.error('Error incrementing smart link use:', error);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Exception in incrementSmartLinkUse:', error);
            return false;
        }
    },

    // Deactivate a smart link (soft delete)
    async deactivateSmartLink(linkId) {
        try {
            const { error } = await supabaseFetch.update('smart_links',
                { is_active: false },
                { eq: { id: linkId } }
            );

            if (error) {
                console.error('Error deactivating smart link:', error);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Exception in deactivateSmartLink:', error);
            return false;
        }
    },

    // Update smart link metadata (expiry, max uses, etc.)
    async updateSmartLink(linkId, updates) {
        try {
            const allowedUpdates = {};

            // Only allow updating specific fields
            if (updates.maxUses !== undefined) allowedUpdates.max_uses = updates.maxUses;
            if (updates.expiresAt !== undefined) allowedUpdates.expires_at = updates.expiresAt;
            if (updates.isActive !== undefined) allowedUpdates.is_active = updates.isActive;
            if (updates.metadata !== undefined) allowedUpdates.metadata = updates.metadata;

            const { error } = await supabaseFetch.update('smart_links',
                allowedUpdates,
                { eq: { id: linkId } }
            );

            if (error) {
                console.error('Error updating smart link:', error);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Exception in updateSmartLink:', error);
            return false;
        }
    },

    // Generate a unique slug suggestion based on target name
    generateSlugSuggestion(targetName) {
        // Remove special characters, convert to lowercase, replace spaces with hyphens
        let slug = targetName
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .substring(0, 50);

        // Add random suffix to ensure uniqueness
        const randomSuffix = Math.random().toString(36).substring(2, 6);
        return `${slug}-${randomSuffix}`;
    },

    // Build full URL for a smart link (prefer short_code over slug)
    getSmartLinkUrl(slugOrCode, baseUrl = null) {
        // The site the member is on (Roadmap #97, R2): no instance address in code.
        const base = baseUrl || window.location.origin;
        return `${base}/l/${slugOrCode}`;
    },

    // Generate label suggestion from target name (truncated to 100 chars)
    generateLabelSuggestion(targetName) {
        if (!targetName) return '';
        return targetName.substring(0, 100);
    }
};

// Export supabase client as named export for direct imports
export { supabase };

// Default export with all services
const dataServiceExports = {
    communityService,
    chatService,
    articleService,
    eventService,
    userService,
    groupService,
    userActionService,
    smartLinkService,
    getOriginalChats,
    getThreadChats,
    getData,
    supabase // Export client for advanced usage
};

export default dataServiceExports;
